import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { WaxTx } from '../App';
import { ArrowLeft, LogOut, Loader, Trophy } from 'lucide-react';
import { useWax } from '../context/WaxContext';

if (typeof document !== 'undefined' && !document.getElementById('gp-kf')) {
  const s = document.createElement('style');
  s.id = 'gp-kf';
  s.textContent = `
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
    @keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
    @keyframes radialIn{from{opacity:0;transform:scale(0.7)}to{opacity:1;transform:scale(1)}}
  `;
  document.head.appendChild(s);
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface PlotState { species: string; plantedAt: number; speciesIndex: number; }
type GameState = Record<number, PlotState>;
interface RentalTree { id: number; plotIndex: number; plantedBy: string; speciesIndex: number; plantedAt: number; species: string; }
type RentalState = Record<number, RentalTree[]>; // keyed by plot_index

// Maturity in seconds and log yield per species_index (matches server TREE_HARVEST)
const ATAVI_LOCKED = true; // reset in progress — re-enable by setting to false
const TREE_HARVEST: Record<number, { maturity_sec: number; logs: number; name: string }> = {
  5: { maturity_sec:  4 * 3600, logs: 20, name: 'Amaranth'     },
  7: { maturity_sec:  8 * 3600, logs: 15, name: 'Vetiver'      },
  4: { maturity_sec: 16 * 3600, logs: 12, name: 'Prickly Pear' },
  3: { maturity_sec: 24 * 3600, logs:  9, name: 'Moringa'      },
  8: { maturity_sec: 36 * 3600, logs:  7, name: 'Jujube'       },
  2: { maturity_sec: 48 * 3600, logs:  5, name: 'Acacia'       },
  1: { maturity_sec: 72 * 3600, logs:  4, name: 'Mesquite'     },
  6: { maturity_sec:120 * 3600, logs:  3, name: 'Neem'         },
};

function fmtDuration(sec: number): string {
  if (sec <= 0) return 'Now';
  if (sec < 3600) return `${Math.ceil(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}
type InfraState = Record<number, string>; // plot_index → infrastructure_type

const SPECIES_INDEX: Record<number,string> = {
  1:'mesquite',2:'acacia',3:'moringa',4:'opuntia',
  5:'amaranth',6:'neem',7:'vetiver',8:'jujube',
};
const SPECIES_ID_TO_INDEX: Record<string,number> = Object.fromEntries(
  Object.entries(SPECIES_INDEX).map(([k,v])=>[v,parseInt(k)])
);

// ─── Constants ────────────────────────────────────────────────────────────────
const TOTAL_PLOTS = 10010;
const ACCOUNT_COLORS = [
  '#f59e0b','#14b8a6','#8b5cf6','#f43f5e','#10b981',
  '#3b82f6','#facc15','#ec4899','#06b6d4','#a78bfa',
  '#fb923c','#34d399','#60a5fa','#f472b6','#4ade80',
];

const SPECIES = [
  { id:'mesquite', name:'Mesquite',     emoji:'🌲', urja:5, color:'#16a34a', desc:'N-Fixer · Shade canopy · Edible pods',         stages:[7,30,90],   mic:[0.5,2,5,10] },
  { id:'acacia',   name:'Acacia',       emoji:'🌳', urja:5, color:'#15803d', desc:'Windbreak · N-Fixer · Fast shade',             stages:[7,25,80],   mic:[0.5,2,4,9]  },
  { id:'moringa',  name:'Moringa',      emoji:'🌿', urja:3, color:'#65a30d', desc:'Fastest canopy · Superfood leaves',            stages:[5,20,60],   mic:[0.5,1.5,4,8]},
  { id:'opuntia',  name:'Prickly Pear', emoji:'🌵', urja:2, color:'#ea580c', desc:'Living fence · Water storage · Edible pads',   stages:[14,60,180], mic:[0.3,1,3,6]  },
  { id:'amaranth', name:'Amaranth',     emoji:'🌱', urja:1, color:'#84cc16', desc:'Fast food crop · Self-seeds · Ground cover',   stages:[5,30,60],   mic:[0.2,0.8,2,4]},
  { id:'neem',     name:'Neem',         emoji:'🌴', urja:6, color:'#0d9488', desc:'Pesticide · Carbon sink · Medicinal',          stages:[14,45,120], mic:[0.5,2,5,12] },
  { id:'vetiver',  name:'Vetiver',      emoji:'🌾', urja:2, color:'#0891b2', desc:'Erosion control · Deep roots · Swale anchor',  stages:[7,30,90],   mic:[0.3,1.5,3,5]},
  { id:'jujube',   name:'Jujube (Ber)', emoji:'🫐', urja:4, color:'#d97706', desc:'Heat tolerant · Early fruiting · Indian desert',stages:[21,60,180], mic:[0.3,1.5,4,8]},
];

const STAGE_NAMES  = ['Seedling','Sapling','Established','Mature'];
const STAGE_COLORS = ['#64748b','#84cc16','#22c55e','#15803d'];

// ─── Season 1 economy ─────────────────────────────────────────────────────────
const SEED_COST: Record<string,number> = {
  amaranth:10, vetiver:20, opuntia:35, moringa:50,
  jujube:70, acacia:90, mesquite:120, neem:160,
};
const FOOD_RATE: Record<string,number> = {
  amaranth:1, vetiver:2, opuntia:3, moringa:5,
  jujube:7, acacia:9, mesquite:11, neem:14,
};
const BHOOMI_RATE_PER_PLOT = 0.1; // per hour

// ─── Canvas iso constants ──────────────────────────────────────────────────────
const TW = 32;   // diamond width
const TH = 16;   // diamond height (2:1)
const TILE_H: Record<string,number> = { av:1, bare:5, s0:8, s1:12, s2:18, s3:25 };

const SPECIES_PAL: Record<string,{top:string;south:string;east:string;glow:string}> = {
  mesquite:{ top:'#22c55e', south:'#166534', east:'#14532d', glow:'#4ade80' },
  acacia:  { top:'#16a34a', south:'#14532d', east:'#052e16', glow:'#22c55e' },
  moringa: { top:'#a3e635', south:'#4d7c0f', east:'#365314', glow:'#bef264' },
  opuntia: { top:'#f97316', south:'#c2410c', east:'#7c2d12', glow:'#fb923c' },
  amaranth:{ top:'#84cc16', south:'#4d7c0f', east:'#365314', glow:'#a3e635' },
  neem:    { top:'#2dd4bf', south:'#0f766e', east:'#042f2e', glow:'#5eead4' },
  vetiver: { top:'#38bdf8', south:'#0369a1', east:'#082f49', glow:'#7dd3fc' },
  jujube:  { top:'#fbbf24', south:'#b45309', east:'#6b3a1f', glow:'#fde68a' },
};

// ─── Canvas tile renderer ──────────────────────────────────────────────────────
function drawIsoTile(
  ctx: CanvasRenderingContext2D,
  col: number, row: number, hPx: number,
  topColor: string, southColor: string, eastColor: string,
  glowColor: string | null, glowStr: number,
  emoji: string | null, emojiSize: number,
  isSelected: boolean, isOrigin: boolean,
  hasOwner: boolean, shimPhase: number,
  outlineOnly = false,
) {
  const cx = (col - row) * TW / 2;
  const groundY = (col + row) * TH / 2;
  const ty = groundY - hPx;

  // East face
  if (hPx > 1) {
    ctx.beginPath();
    ctx.moveTo(cx + TW/2, ty);
    ctx.lineTo(cx, ty + TH/2);
    ctx.lineTo(cx, groundY + TH/2);
    ctx.lineTo(cx + TW/2, groundY);
    ctx.closePath();
    ctx.fillStyle = eastColor;
    ctx.fill();
  }

  // South face
  if (hPx > 1) {
    ctx.beginPath();
    ctx.moveTo(cx - TW/2, ty);
    ctx.lineTo(cx, ty + TH/2);
    ctx.lineTo(cx, groundY + TH/2);
    ctx.lineTo(cx - TW/2, groundY);
    ctx.closePath();
    ctx.fillStyle = southColor;
    ctx.fill();
  }

  // Top face glow
  if (glowColor && glowStr > 0) { ctx.shadowColor = glowColor; ctx.shadowBlur = glowStr; }

  // Top face diamond
  ctx.beginPath();
  ctx.moveTo(cx, ty - TH/2);
  ctx.lineTo(cx + TW/2, ty);
  ctx.lineTo(cx, ty + TH/2);
  ctx.lineTo(cx - TW/2, ty);
  ctx.closePath();
  ctx.fillStyle = topColor;
  ctx.fill();
  ctx.shadowBlur = 0;

  // Shimmer on owned tiles
  if (hasOwner && shimPhase >= 0) {
    const shimX = cx - TW / 2 + shimPhase * TW * 2.6;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, ty - TH/2);
    ctx.lineTo(cx + TW/2, ty);
    ctx.lineTo(cx, ty + TH/2);
    ctx.lineTo(cx - TW/2, ty);
    ctx.closePath();
    ctx.clip();
    const sg = ctx.createLinearGradient(shimX - 7, ty, shimX + 7, ty);
    sg.addColorStop(0, 'rgba(255,255,255,0)');
    sg.addColorStop(0.5, 'rgba(255,255,255,0.18)');
    sg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sg;
    ctx.fillRect(cx - TW, ty - TH, TW * 2, TH * 2);
    ctx.restore();
  }

  // Origin marker
  if (isOrigin && !emoji) {
    ctx.font = '7px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = hasOwner ? glowColor + '88' || '#f59e0b88' : '#f59e0b33';
    ctx.fillText('⊕', cx, ty);
  }

  // Emoji tree
  if (emoji) {
    ctx.font = `${emojiSize}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (glowColor) { ctx.shadowColor = glowColor; ctx.shadowBlur = 10; }
    ctx.fillText(emoji, cx, ty - 2);
    ctx.shadowBlur = 0;
  }

  // Selection outline
  if (isSelected) {
    if (outlineOnly) {
      // Thin clean white border — no blur, no glow
      ctx.lineWidth = 1;
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      if (hPx > 1) {
        ctx.beginPath();
        ctx.moveTo(cx + TW/2, ty); ctx.lineTo(cx, ty + TH/2);
        ctx.lineTo(cx, groundY + TH/2); ctx.lineTo(cx + TW/2, groundY);
        ctx.closePath(); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - TW/2, ty); ctx.lineTo(cx, ty + TH/2);
        ctx.lineTo(cx, groundY + TH/2); ctx.lineTo(cx - TW/2, groundY);
        ctx.closePath(); ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(cx, ty - TH/2); ctx.lineTo(cx + TW/2, ty);
      ctx.lineTo(cx, ty + TH/2); ctx.lineTo(cx - TW/2, ty);
      ctx.closePath(); ctx.stroke();
    } else if (glowColor) {
      ctx.lineWidth = 1.5;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 18;
      const gc = glowColor + 'dd';
      ctx.strokeStyle = gc;
      if (hPx > 1) {
        ctx.beginPath();
        ctx.moveTo(cx + TW/2, ty); ctx.lineTo(cx, ty + TH/2);
        ctx.lineTo(cx, groundY + TH/2); ctx.lineTo(cx + TW/2, groundY);
        ctx.closePath(); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - TW/2, ty); ctx.lineTo(cx, ty + TH/2);
        ctx.lineTo(cx, groundY + TH/2); ctx.lineTo(cx - TW/2, groundY);
        ctx.closePath(); ctx.stroke();
      }
      const pad = 3, padV = pad * (TH / TW);
      ctx.shadowBlur = 22;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, ty - TH/2 - padV); ctx.lineTo(cx + TW/2 + pad, ty);
      ctx.lineTo(cx, ty + TH/2 + padV); ctx.lineTo(cx - TW/2 - pad, ty);
      ctx.closePath(); ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function generateSpiral(n: number): [number,number][] {
  const c: [number,number][] = [];
  if (!n) return c;
  c.push([0,0]);
  let x=0,y=0,step=1,dir=0;
  const dx=[1,0,-1,0], dy=[0,1,0,-1];
  while (c.length < n) {
    for (let rep=0;rep<2&&c.length<n;rep++) {
      for (let i=0;i<step&&c.length<n;i++){x+=dx[dir];y+=dy[dir];c.push([x,y]);}
      dir=(dir+1)%4;
    }
    step++;
  }
  return c;
}

function getStage(speciesId: string, plantedAt: number): 0|1|2|3 {
  const sp = SPECIES.find(s=>s.id===speciesId);
  if (!sp) return 0;
  const d = (Date.now()-plantedAt)/86_400_000;
  if (d>=sp.stages[2]) return 3;
  if (d>=sp.stages[1]) return 2;
  if (d>=sp.stages[0]) return 1;
  return 0;
}

function plotsToGameState(plots: any[]): { gs: GameState; infra: InfraState } {
  const gs: GameState = {};
  const infra: InfraState = {};
  for (const p of plots) {
    if (p.species_index != null && p.planted_at != null) {
      const id = SPECIES_INDEX[p.species_index];
      if (id) gs[p.plot_index] = { species: id, plantedAt: new Date(p.planted_at).getTime(), speciesIndex: p.species_index };
    }
    if (p.infrastructure_type != null) {
      infra[p.plot_index] = p.infrastructure_type;
    }
  }
  return { gs, infra };
}

const INFRA_PAL: Record<string, { top: string; south: string; east: string; glow: string; emoji: string }> = {
  water_harvester: { top: '#0e7490cc', south: '#0c607566', east: '#0c607533', glow: '#06b6d4', emoji: '💧' },
  handcart:        { top: '#c2410ccc', south: '#9a330a66', east: '#7a280833', glow: '#f97316', emoji: '🛒' },
};

interface TileDatum {
  col: number; row: number;
  plotIdx: number | null;
  owner: string | null;
  ownerColor: string | null;
  ps: PlotState | null;
  sp: typeof SPECIES[0] | null;
  pal: typeof SPECIES_PAL[string] | null;
  infraType: string | null;
  isOrigin: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────
const GamePage: React.FC<{ txs: WaxTx[]; onBack: ()=>void; onLeaderboard: ()=>void }> = ({ txs, onBack, onLeaderboard }) => {

  const allCoords = useMemo(()=>generateSpiral(TOTAL_PLOTS),[]);

  const { minX, maxX, minY, maxY, gridW, gridH } = useMemo(()=>{
    const xs=allCoords.map(c=>c[0]), ys=allCoords.map(c=>c[1]);
    const pad=1;
    const minX=Math.min(...xs)-pad, maxX=Math.max(...xs)+pad; // eslint-disable-line @typescript-eslint/no-unused-vars
    const minY=Math.min(...ys)-pad, maxY=Math.max(...ys)+pad; // eslint-disable-line @typescript-eslint/no-unused-vars
    return {minX,minY,maxX,maxY,gridW:maxX-minX+1,gridH:maxY-minY+1};
  },[allCoords]);

  const coordIdx = useMemo(()=>{
    const m:Record<string,number>={};
    allCoords.forEach((c,i)=>{m[`${c[0]},${c[1]}`]=i;});
    return m;
  },[allCoords]);

  const {ownerByPlot,acctColor,acctCount,totalOwned} = useMemo(()=>{
    const ordered=[...txs].reverse();
    const ownerByPlot:Record<number,string>={};
    const order:string[]=[];
    let idx=0;
    ordered.forEach(tx=>{
      const n=tx.plots_count!==undefined?tx.plots_count:Math.max(1,Math.floor(tx.amount));
      for(let k=0;k<n;k++){
        if(idx<TOTAL_PLOTS){ownerByPlot[idx]=tx.from;if(!order.includes(tx.from))order.push(tx.from);idx++;}
      }
    });
    const acctColor:Record<string,string>={};
    order.forEach((a,i)=>{acctColor[a]=ACCOUNT_COLORS[i%ACCOUNT_COLORS.length];});
    const acctCount:Record<string,number>={};
    Object.values(ownerByPlot).forEach(o=>{acctCount[o]=(acctCount[o]||0)+1;});
    return {ownerByPlot,acctColor,acctCount,totalOwned:idx};
  },[txs]);

  const [gs, setGs] = useState<GameState>({});
  const [infraGs, setInfraGs] = useState<InfraState>({});
  const [myInfra, setMyInfra] = useState<{ machine_owned: boolean; handcart_owned: boolean; placed: Record<string, number> }>({ machine_owned: false, handcart_owned: false, placed: {} });
  const [placingItem, setPlacingItem] = useState<string | null>(null); // item being placed
  const [plotsLoading, setPlotsLoading] = useState(true);
  const [showMyPlotsOnly, setShowMyPlotsOnly] = useState(false);
  const [showHarvestableOnly, setShowHarvestableOnly] = useState(false);
  const [mobilePlotSheet, setMobilePlotSheet] = useState<number|null>(null);

  const fetchPlots = useCallback(async () => {
    try {
      const res = await fetch('/samatvam/api/samatvam/land/plots');
      const data = await res.json();
      if (data.success) {
        const { gs: newGs, infra } = plotsToGameState(data.plots);
        setGs(newGs);
        setInfraGs(infra);
      }
    } catch {}
    finally { setPlotsLoading(false); }
  },[]);

  const fetchMyInfra = useCallback(async (acct: string) => {
    if (!acct) return;
    try {
      const r = await fetch(`/samatvam/api/samatvam/infrastructure/${acct}`);
      const d = await r.json();
      if (d.success) setMyInfra({ machine_owned: d.machine_installed, handcart_owned: d.handcart_owned, placed: d.placed || {} });
    } catch {}
  }, []);

  useEffect(()=>{ fetchPlots(); },[fetchPlots]);

  // ── Rental system state ──────────────────────────────────────────────────────
  const [gsRental, setGsRental] = useState<RentalState>({});
  const [mySeedCount, setMySeedCount] = useState(0);
  const [buySeedStatus, setBuySeedStatus] = useState<'idle'|'sending'|'verifying'|'done'|'error'>('idle');
  const [buySeedMsg, setBuySeedMsg] = useState('');
  const [buySeedQty, setBuySeedQty] = useState('1');
  const [plantSeedModal, setPlantSeedModal] = useState<{plotIdx:number;landowner:string;slots:number}|null>(null);
  const [plantSeedSpecies, setPlantSeedSpecies] = useState<number|null>(null);
  const [plantSeedStatus, setPlantSeedStatus] = useState<'idle'|'sending'|'success'|'error'>('idle');
  const [plantSeedMsg, setPlantSeedMsg] = useState('');

  const fetchRentalTrees = useCallback(async () => {
    try {
      const r = await fetch('/samatvam/api/samatvam/land/rental-trees');
      const d = await r.json();
      if (d.success) {
        const map: RentalState = {};
        for (const t of d.trees) {
          const sp = SPECIES_INDEX[t.species_index] || '';
          const tree: RentalTree = { id: t.id, plotIndex: t.plot_index, plantedBy: t.planted_by, speciesIndex: t.species_index, plantedAt: new Date(t.planted_at).getTime(), species: sp };
          if (!map[t.plot_index]) map[t.plot_index] = [];
          map[t.plot_index].push(tree);
        }
        setGsRental(map);
      }
    } catch {}
  }, []);

  const fetchSeedCount = useCallback(async (acct: string) => {
    if (!acct) return;
    try {
      const r = await fetch(`/samatvam/api/samatvam/seeds/${acct}`);
      const d = await r.json();
      if (d.success) setMySeedCount(d.seeds_in_wallet || 0);
    } catch {}
  }, []);

  useEffect(()=>{ fetchRentalTrees(); },[fetchRentalTrees]);

  const setPlot = useCallback(async (idx: number, ps: PlotState|null, waxAcct: string) => {
    if (ps) {
      const si = SPECIES_ID_TO_INDEX[ps.species]; if (!si) return;
      const r = await fetch('/samatvam/api/samatvam/plot/plant',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({plot_index:idx,wax_account:waxAcct,species_index:si})});
      if (!r.ok) return;
    } else {
      const r = await fetch('/samatvam/api/samatvam/plot/remove',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({plot_index:idx,wax_account:waxAcct})});
      if (!r.ok) return;
      const data = await r.json();
      if (data.refund > 0) {
        setBhoomAccum(prev => prev + data.refund);
        setToast({id:Date.now(), emoji:'🪙', name:`+${data.refund} Bhoomi refunded`});
        toastTimer.current = setTimeout(()=>setToast(null), 2500);
      }
    }
    setGs(prev=>{ const next={...prev}; if(ps) next[idx]=ps; else delete next[idx]; return next; });
  },[]);

  // WAX wallet — session managed globally by WaxContext
  const { waxSession, waxActor, waxConnecting, connect: waxConnect, disconnect: waxDisconnect } = useWax();
  const [waxError, setWaxError] = useState('');

  // Platform (Ahantravyuh) login — no auto-connect, user must click
  const [profileActor, setProfileActor] = useState('');     // ahn_id used as game identity
  const [profileDisplay, setProfileDisplay] = useState(''); // name#shortid shown in UI

  const handleWaxConnect = async () => {
    if (profileActor) return; // already connected via profile
    setWaxError('');
    try { await waxConnect(); }
    catch(err:any){ setWaxError(err.message||'Connection cancelled'); }
  };

  const handleWaxDisconnect = async () => {
    await waxDisconnect();
    setWaxError('');
  };

  const handleProfileConnect = () => {
    if (waxActor) return; // already connected via WAX
    try {
      const raw = localStorage.getItem('samatvam_user') || localStorage.getItem('user');
      if (!raw) { setWaxError('No platform session found. Please log in first.'); return; }
      const u = JSON.parse(raw);
      const id: string = u.ahn_id || u.id || '';
      const name: string = u.name || u.username || 'Player';
      if (!id) { setWaxError('Could not read account ID.'); return; }
      const shortId = id.replace(/-/g,'').slice(-4);
      setProfileActor(id);
      setProfileDisplay(`${name}#${shortId}`);
    } catch { setWaxError('Could not read platform session.'); }
  };

  const handleProfileDisconnect = () => {
    setProfileActor(''); setProfileDisplay('');
  };

  // Active game identity — WAX address or ahn_id, never both
  const myAcct = waxActor || profileActor;

  // Persist active account to localStorage so other pages (Warehouse etc.) read the same identity
  useEffect(() => {
    if (myAcct) localStorage.setItem('samatvam_active_account', myAcct);
    else localStorage.removeItem('samatvam_active_account');
  }, [myAcct]);

  useEffect(()=>{ if(myAcct) fetchMyInfra(myAcct); },[myAcct,fetchMyInfra]);
  useEffect(()=>{ if(myAcct) fetchSeedCount(myAcct); },[myAcct,fetchSeedCount]);

  const handlePlantSeed = useCallback(async () => {
    if (!plantSeedModal || plantSeedSpecies === null || !myAcct) return;
    setPlantSeedStatus('sending'); setPlantSeedMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/plant-seed', {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ plot_index: plantSeedModal.plotIdx, wax_account: myAcct, species_index: plantSeedSpecies }),
      });
      const d = await r.json();
      if (!r.ok) { setPlantSeedStatus('error'); setPlantSeedMsg(d.error || 'Failed'); return; }
      setPlantSeedStatus('success');
      setMySeedCount(p => Math.max(0, p - 1));
      setPlantSeedMsg(`✅ Planted! Earning 0.8 Food/hr${plantSeedModal.landowner !== myAcct ? ` · Landowner: ${plantSeedModal.landowner}` : ''}`);
      await fetchRentalTrees();
      setTimeout(() => { setPlantSeedModal(null); setPlantSeedStatus('idle'); setPlantSeedMsg(''); setPlantSeedSpecies(null); }, 2000);
    } catch { setPlantSeedStatus('error'); setPlantSeedMsg('Network error'); }
  }, [plantSeedModal, plantSeedSpecies, myAcct, fetchRentalTrees]);


  // Selected plot
  const [sel, setSel] = useState<number|null>(null);
  const selRef = useRef<number|null>(null);
  useEffect(()=>{ selRef.current=sel; },[sel]);

  // Mobile
  const [isMobile, setIsMobile] = useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [mobilePlantOpen, setMobilePlantOpen] = useState(false);
  const [mobilePlantTarget, setMobilePlantTarget] = useState<number|null>(null);
  useEffect(()=>{ const h=()=>setIsMobile(window.innerWidth<768); window.addEventListener('resize',h); return()=>window.removeEventListener('resize',h); },[]);

  // Lite mode — strips heavy canvas effects (glow, shimmer, stars, embers, aurora)
  const [liteMode, setLiteMode] = useState(()=>{
    if(typeof window==='undefined') return false;
    const saved=localStorage.getItem('samatvam_lite_mode');
    if(saved!==null) return saved==='1';
    return window.innerWidth<768; // default ON for mobile
  });
  const liteModeRef = useRef(liteMode);
  useEffect(()=>{ liteModeRef.current=liteMode; localStorage.setItem('samatvam_lite_mode',liteMode?'1':'0'); },[liteMode]);

  // Pan & zoom — state for UI buttons, refs for canvas loop
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({x:0,y:0});
  const zoomRef = useRef(1);
  const panRef  = useRef({x:0,y:0});
  useEffect(()=>{ zoomRef.current=zoom; },[zoom]);
  useEffect(()=>{ panRef.current=pan; },[pan]);

  // Canvas + wrap refs
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const wrapRef    = useRef<HTMLDivElement>(null);
  const drag  = useRef({active:false,sx:0,sy:0,px:0,py:0,moved:false});
  const pinch = useRef({active:false,dist:0,mx:0,my:0,px:0,py:0});

  // Iso grid offset — puts origin tile (plot 0) at canvas centre
  const {isoOffX, isoOffY} = useMemo(()=>{
    const oCol = -minX, oRow = maxY;
    return { isoOffX: -((oCol-oRow)*TW/2), isoOffY: -((oCol+oRow)*TH/2) };
  },[minX,maxY]);
  const isoOffXRef = useRef(isoOffX);
  const isoOffYRef = useRef(isoOffY);
  useEffect(()=>{ isoOffXRef.current=isoOffX; },[isoOffX]);
  useEffect(()=>{ isoOffYRef.current=isoOffY; },[isoOffY]);

  const showMyPlotsOnlyRef = useRef(false);
  const showHarvestableOnlyRef = useRef(false);
  const myAcctRef = useRef('');
  useEffect(()=>{ showMyPlotsOnlyRef.current=showMyPlotsOnly; },[showMyPlotsOnly]);
  useEffect(()=>{ showHarvestableOnlyRef.current=showHarvestableOnly; },[showHarvestableOnly]);
  useEffect(()=>{ myAcctRef.current=myAcct; },[myAcct]);

  const infraGsRef = useRef<InfraState>({});
  useEffect(()=>{ infraGsRef.current=infraGs; },[infraGs]);

  const gsRentalRef = useRef<RentalState>({});
  useEffect(()=>{ gsRentalRef.current=gsRental; },[gsRental]);

  // Hit-test helper — world coords → plotIdx
  const hitTest = useCallback((mx:number,my:number,canvasW:number,canvasH:number):{plotIdx:number|null;owner:string|null;hasTree:boolean;hasInfra:boolean}=>{
    const z=zoomRef.current, p=panRef.current;
    const wx=(mx-canvasW/2-p.x)/z - isoOffXRef.current;
    const wy=(my-canvasH/2-p.y)/z - isoOffYRef.current;
    const a=wx/(TW/2), b=wy/(TH/2);
    const col=Math.round((a+b)/2), row=Math.round((b-a)/2);
    if(col<0||col>=gridW||row<0||row>=gridH) return {plotIdx:null,owner:null,hasTree:false,hasInfra:false};
    const gx=minX+col, gy=maxY-row;
    const plotIdx=coordIdx[`${gx},${gy}`]??null;
    if(plotIdx===null) return {plotIdx:null,owner:null,hasTree:false,hasInfra:false};
    const owner=ownerByPlot[plotIdx]??null;
    const hasTree=!!gs[plotIdx];
    const hasInfra=!!infraGsRef.current[plotIdx];
    return {plotIdx,owner,hasTree,hasInfra};
  },[gridW,gridH,minX,maxY,coordIdx,ownerByPlot,gs]);

  // Mouse handlers
  const onMD = useCallback((e:React.MouseEvent)=>{
    drag.current={active:true,sx:e.clientX,sy:e.clientY,px:pan.x,py:pan.y,moved:false};
  },[pan]);

  const onMM = useCallback((e:React.MouseEvent)=>{
    if(!drag.current.active) return;
    const dx=e.clientX-drag.current.sx, dy=e.clientY-drag.current.sy;
    if(Math.abs(dx)>3||Math.abs(dy)>3) drag.current.moved=true;
    if(drag.current.moved) setPan({x:drag.current.px+dx,y:drag.current.py+dy});
  },[]);

  const onMU = useCallback((e:React.MouseEvent)=>{
    if(!drag.current.active) return;
    const wasDrag=drag.current.moved; drag.current.active=false;
    if(wasDrag) return;
    const canvas=canvasRef.current; if(!canvas) return;
    const rect=canvas.getBoundingClientRect();
    const mx=e.clientX-rect.left, my=e.clientY-rect.top;
    const {plotIdx,owner,hasTree,hasInfra}=hitTest(mx,my,rect.width,rect.height);
    if(plotIdx===null){setSel(null);setRadialMenu(null);setMobilePlotSheet(null);return;}
    const isMineCell=!!(myAcct&&owner&&owner===myAcct);
    if(isMineCell&&!hasTree&&!hasInfra){
      setSel(plotIdx); setRadialMenu({plotIdx,x:e.clientX,y:e.clientY});
    } else {
      setSel(selRef.current===plotIdx?null:plotIdx); setRadialMenu(null);
    }
  },[hitTest,myAcct]);

  const onWh = useCallback((e:React.WheelEvent)=>{
    e.preventDefault();
    const canvas=canvasRef.current; if(!canvas) return;
    const rect=canvas.getBoundingClientRect();
    const mx=e.clientX-rect.left-rect.width/2;
    const my=e.clientY-rect.top-rect.height/2;
    const oldZ=zoomRef.current;
    const newZ=Math.max(0.3,Math.min(4,oldZ-e.deltaY*0.001));
    const sc=newZ/oldZ, p=panRef.current;
    setPan({x:mx-(mx-p.x)*sc, y:my-(my-p.y)*sc});
    setZoom(newZ);
  },[]);

  // Non-passive touchmove on canvas so preventDefault blocks browser zoom/scroll
  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas) return;
    const handler=(e:TouchEvent)=>{ if(e.touches.length>1) e.preventDefault(); };
    canvas.addEventListener('touchmove',handler,{passive:false});
    return()=>canvas.removeEventListener('touchmove',handler);
  },[]);

  // Touch
  const onTE = useCallback((e:React.TouchEvent)=>{
    if(e.touches.length===1){
      const t=e.touches[0];
      drag.current={active:true,sx:t.clientX,sy:t.clientY,px:pan.x,py:pan.y,moved:false};
      pinch.current.active=false;
    } else if(e.touches.length===2){
      const t1=e.touches[0],t2=e.touches[1];
      const dx=t2.clientX-t1.clientX,dy=t2.clientY-t1.clientY;
      const dist=Math.sqrt(dx*dx+dy*dy);
      pinch.current={active:true,dist,mx:(t1.clientX+t2.clientX)/2,my:(t1.clientY+t2.clientY)/2,px:pan.x,py:pan.y};
      drag.current.active=false;
    }
  },[pan]);

  const onTM = useCallback((e:React.TouchEvent)=>{
    if(e.touches.length===2&&pinch.current.active){
      const t1=e.touches[0],t2=e.touches[1];
      const dx=t2.clientX-t1.clientX,dy=t2.clientY-t1.clientY;
      const newDist=Math.sqrt(dx*dx+dy*dy);
      const scale=newDist/pinch.current.dist;
      const canvas=canvasRef.current; if(!canvas) return;
      const rect=canvas.getBoundingClientRect();
      // Midpoint relative to canvas centre
      const newMx=(t1.clientX+t2.clientX)/2,newMy=(t1.clientY+t2.clientY)/2;
      const cx=newMx-rect.left-rect.width/2;
      const cy=newMy-rect.top-rect.height/2;
      const oldZ=zoomRef.current;
      const newZ=Math.max(0.3,Math.min(4,oldZ*scale));
      const sc=newZ/oldZ,p=panRef.current;
      setPan({x:cx-(cx-p.x)*sc,y:cy-(cy-p.y)*sc});
      setZoom(newZ);
      pinch.current.dist=newDist;
    } else if(e.touches.length===1&&drag.current.active){
      const t=e.touches[0];
      const dx=t.clientX-drag.current.sx,dy=t.clientY-drag.current.sy;
      if(Math.abs(dx)>12||Math.abs(dy)>12) drag.current.moved=true;
      if(drag.current.moved) setPan({x:drag.current.px+dx,y:drag.current.py+dy});
    }
  },[]);

  const onTEnd = useCallback((e:React.TouchEvent)=>{
    // Two fingers lifted → reset pinch, resume single-touch pan from current position
    if(pinch.current.active){
      pinch.current.active=false;
      if(e.touches.length===1){
        const t=e.touches[0];
        drag.current={active:true,sx:t.clientX,sy:t.clientY,px:panRef.current.x,py:panRef.current.y,moved:false};
      }
      return;
    }
    if(!drag.current.active) return;
    const wasDrag=drag.current.moved; drag.current.active=false;
    if(wasDrag||e.changedTouches.length===0) return;
    const t=e.changedTouches[0];
    const canvas=canvasRef.current; if(!canvas) return;
    const rect=canvas.getBoundingClientRect();
    const mx=t.clientX-rect.left,my=t.clientY-rect.top;
    const {plotIdx,owner,hasTree,hasInfra}=hitTest(mx,my,rect.width,rect.height);
    if(plotIdx===null){setSel(null);setRadialMenu(null);setMobilePlotSheet(null);return;}
    // Mobile: always open bottom sheet for any plot tap
    setSel(plotIdx);
    setMobilePlotSheet(plotIdx);
    setRadialMenu(null);
  },[hitTest,myAcct]);

  // Tile draw data (memoized, sorted back→front)
  const tileData = useMemo(():TileDatum[]=>{
    const arr:TileDatum[]=[];
    for(let rowIdx=0;rowIdx<gridH;rowIdx++){
      for(let colIdx=0;colIdx<gridW;colIdx++){
        const gx=minX+colIdx, gy=maxY-rowIdx;
        const key=`${gx},${gy}`;
        const plotIdx=coordIdx[key]??null;
        const owner=plotIdx!==null?ownerByPlot[plotIdx]??null:null;
        const ps=plotIdx!==null?gs[plotIdx]??null:null;
        const infraType=plotIdx!==null?infraGs[plotIdx]??null:null;
        const sp=ps?SPECIES.find(s=>s.id===ps.species)??null:null;
        const pal=sp?(SPECIES_PAL[ps!.species]??SPECIES_PAL.mesquite):null;
        const ownerColor=owner?acctColor[owner]??null:null;
        arr.push({col:colIdx,row:rowIdx,plotIdx,owner,ownerColor,ps,sp,pal,infraType,isOrigin:gx===0&&gy===0});
      }
    }
    arr.sort((a,b)=>(a.col+a.row)-(b.col+b.row));
    return arr;
  },[gridH,gridW,minX,maxY,coordIdx,ownerByPlot,gs,infraGs,acctColor]);

  const tileDataRef = useRef(tileData);
  useEffect(()=>{ tileDataRef.current=tileData; },[tileData]);

  // ─── Canvas animation loop ────────────────────────────────────────────────
  useEffect(()=>{
    const canvas=canvasRef.current, wrap=wrapRef.current;
    if(!canvas||!wrap) return;

    const stars=Array.from({length:60},(_,i)=>({
      x:((i*1731+17)%100)/100, y:((i*2311+23)%65)/100,
      big:i%10===0, phase:(i*0.3)%3, speed:2+(i%4),
    }));
    const EC=['#f59e0b','#06b6d4','#8b5cf6','#10b981','#f97316'];
    const embers=Array.from({length:10},(_,i)=>({
      x:(30+(i*137+11)%40)/100, color:EC[i%EC.length],
      big:i%3===0, period:(3.5+(i%5)*0.6)*1000, delay:(i*0.55)%4*1000,
    }));

    const dpr=window.devicePixelRatio||1;
    let rafId:number;

    const frame=(now:number)=>{
      const rect=wrap.getBoundingClientRect();
      const W=rect.width, H=rect.height;
      if(Math.round(canvas.width)!==Math.round(W*dpr)||Math.round(canvas.height)!==Math.round(H*dpr)){
        canvas.width=Math.round(W*dpr); canvas.height=Math.round(H*dpr);
        canvas.style.width=W+'px'; canvas.style.height=H+'px';
      }
      const ctx=canvas.getContext('2d')!;
      ctx.setTransform(dpr,0,0,dpr,0,0);

      const lite=liteModeRef.current;

      // Background
      ctx.fillStyle='#020617';
      ctx.fillRect(0,0,W,H);

      if(!lite){
        // Aurora sweep
        const ap=(now*0.000033)%1;
        const ag=ctx.createLinearGradient(ap*W*4,0,(ap+0.25)*W*4,H);
        ag.addColorStop(0,'rgba(26,5,53,0.75)');
        ag.addColorStop(0.25,'rgba(4,13,42,0.7)');
        ag.addColorStop(0.5,'rgba(3,26,14,0.55)');
        ag.addColorStop(0.75,'rgba(26,12,3,0.65)');
        ag.addColorStop(1,'rgba(26,5,53,0.75)');
        ctx.fillStyle=ag; ctx.fillRect(0,0,W,H);

        // Radial city glow at origin
        const p0=panRef.current;
        const gcx=W/2+p0.x, gcy=H/2+p0.y;
        const rg=ctx.createRadialGradient(gcx,gcy,0,gcx,gcy,Math.max(W,H)*0.55);
        rg.addColorStop(0,'rgba(245,158,11,0.08)');
        rg.addColorStop(0.4,'rgba(139,92,246,0.04)');
        rg.addColorStop(1,'rgba(0,0,0,0)');
        ctx.fillStyle=rg; ctx.fillRect(0,0,W,H);

        // Stars
        for(const star of stars){
          const alpha=0.15+0.7*(0.5+0.5*Math.sin(now*0.001/star.speed+star.phase));
          ctx.beginPath();
          ctx.arc(star.x*W,star.y*H*0.65,star.big?1.5:0.75,0,Math.PI*2);
          ctx.fillStyle=`rgba(255,255,255,${alpha.toFixed(2)})`;
          if(star.big){ctx.shadowColor='rgba(255,255,255,0.4)';ctx.shadowBlur=4;}
          ctx.fill();
          if(star.big) ctx.shadowBlur=0;
        }

        // Embers
        for(const ember of embers){
          const cycle=((now+ember.delay)%ember.period)/ember.period;
          if(cycle>0.85) continue;
          const alpha=0.8*(1-cycle/0.85);
          ctx.beginPath();
          ctx.arc(ember.x*W,H*0.75-cycle*100,ember.big?2:1.5,0,Math.PI*2);
          ctx.globalAlpha=alpha;
          ctx.fillStyle=ember.color;
          ctx.shadowColor=ember.color; ctx.shadowBlur=6;
          ctx.fill();
          ctx.shadowBlur=0; ctx.globalAlpha=1;
        }
      }

      // ── Tiles ──
      const p=panRef.current;
      const z=zoomRef.current;
      const offX=isoOffXRef.current, offY=isoOffYRef.current;
      const selIdx=selRef.current;
      ctx.save();
      ctx.translate(W/2+p.x, H/2+p.y);
      ctx.scale(z,z);
      ctx.translate(offX,offY);

      // ── Tiles ──
      const hasSel=selIdx!==null;
      for(const tile of tileDataRef.current){
        const {col,row,plotIdx,owner,ownerColor,ps,sp,pal,infraType,isOrigin}=tile;
        const infraPal = infraType ? INFRA_PAL[infraType] ?? null : null;

        // All trees on this plot (bhoomi + rental, unified)
        const rentalTrees=gsRentalRef.current[plotIdx??-1]??[];
        const allTreeEmojis:string[]=[];
        if(sp) allTreeEmojis.push(sp.emoji);
        for(const rt of rentalTrees){ const rtSp=SPECIES.find(s=>s.id===rt.species); if(rtSp) allTreeEmojis.push(rtSp.emoji); }

        // Height — bhoomi uses maturity stage; rental-only uses count
        let hKey = owner ? 'bare' : 'av';
        if(ps){ const st=getStage(ps.species,ps.plantedAt); hKey=`s${st}`; }
        else if(rentalTrees.length>0){ hKey=rentalTrees.length>=4?'s3':rentalTrees.length>=3?'s2':rentalTrees.length>=2?'s1':'s0'; }
        if(infraType) hKey = 's1';
        const hPx=TILE_H[hKey]??TILE_H.av;

        const isSelected=plotIdx===selIdx;
        const base=ownerColor||'#f59e0b';
        let topC:string,southC:string,eastC:string,glowC:string|null;

        if(infraPal){
          topC=infraPal.top; southC=infraPal.south; eastC=infraPal.east; glowC=infraPal.glow;
        } else if(pal){
          topC=pal.top; southC=pal.south; eastC=pal.east; glowC=pal.glow;
        } else if(owner){
          topC=base+'88'; southC=base+'44'; eastC=base+'22'; glowC=base;
        } else {
          topC='#c49038'; southC='#8a6420'; eastC='#6b4c18'; glowC=isOrigin?'#f59e0b':null;
        }

        // Brighten selected tile's faces
        if(isSelected&&owner){
          topC=glowC??base;
          southC=(infraPal??pal)?.south ?? base+'88';
          eastC=(infraPal??pal)?.east ?? base+'44';
        }

        const glowAmt=lite?0.6:(0.4+0.6*(0.5+0.5*Math.sin(now*0.002+(plotIdx??0)*0.27)));
        const glowStr=lite?(isSelected?10:0):isSelected?22:glowAmt*((infraPal||pal||allTreeEmojis.length>0)?14:9);
        const shimPhase=lite?-1:(((now*0.4+(plotIdx??0)*41)%3000)/3000);
        const emoji = infraPal?.emoji ?? null; // trees drawn separately below for all modes
        const stage=ps?getStage(ps.species,ps.plantedAt):null;
        const emojiSize = 13; // only used for infra emoji now

        // Dim non-selected tiles in normal mode; lite mode skips the dim
        const _showMine=showMyPlotsOnlyRef.current, _myA=myAcctRef.current;
        const _showHarv=showHarvestableOnlyRef.current;
        const isMyTile=!!(_myA)&&owner===_myA;
        let isHarvestable=false;
        if(_showHarv&&isMyTile&&ps){
          const cfg=TREE_HARVEST[ps.speciesIndex];
          if(cfg){ const elapsed=(Date.now()-ps.plantedAt)/1000; isHarvestable=elapsed>=cfg.maturity_sec; }
        }
        let tileAlpha=1;
        if(!lite&&hasSel&&!isSelected) tileAlpha=0.22;
        else if(_showHarv&&!isHarvestable) tileAlpha=0.06;
        else if(_showMine&&!isMyTile) tileAlpha=0.06;
        if(tileAlpha!==1) ctx.globalAlpha=tileAlpha;
        drawIsoTile(ctx,col,row,hPx,topC,southC,eastC,glowC,glowStr,emoji,emojiSize,isSelected,isOrigin,!!owner,shimPhase,lite);
        if(tileAlpha!==1) ctx.globalAlpha=1;

        // Crammed tree emojis (all sources unified — bhoomi + rental)
        if(allTreeEmojis.length>0&&!infraPal){
          const cx2=(col-row)*TW/2;
          const cy2=(col+row)*TH/2-hPx;
          const OFFSETS=[
            [[0,0]],
            [[-5,1],[5,-1]],
            [[-5,-2],[5,-2],[0,4]],
            [[-6,-2],[6,-2],[-4,4],[4,4]],
            [[-7,-3],[0,-3],[7,-3],[-4,4],[4,4]],
          ];
          const n=Math.min(allTreeEmojis.length,5);
          const sz=n===1?(stage===3?15:stage===2?13:stage===1?11:9):8;
          const offs=OFFSETS[n-1];
          ctx.save();
          ctx.font=`${sz}px serif`;
          ctx.textAlign='center'; ctx.textBaseline='middle';
          for(let i=0;i<n;i++){
            const [ox,oy]=offs[i];
            ctx.fillText(allTreeEmojis[i],cx2+ox,cy2-2+oy);
          }
          ctx.restore();
        }
      }

      ctx.restore();

      rafId=requestAnimationFrame(frame);
    };

    rafId=requestAnimationFrame(frame);
    return ()=>cancelAnimationFrame(rafId);
  },[tileData]); // restarts when tile data changes; pan/zoom/sel read from refs

  // ─── Stats ────────────────────────────────────────────────────────────────
  const {planted,spCounts}=useMemo(()=>{
    let planted=0; const spCounts:Record<string,number>={};
    Object.entries(gs).forEach(([k,ps])=>{ const i=parseInt(k); if(!ownerByPlot[i]) return; planted++; spCounts[ps.species]=(spCounts[ps.species]||0)+1; });
    return {planted,spCounts};
  },[gs,ownerByPlot]);

  const {myPlanted,bhoomRate,foodRate}=useMemo(()=>{
    if(!myAcct) return {myPlanted:0,bhoomRate:0,foodRate:0};
    let myPlanted=0,foodRate=0;
    const myTotal=Object.values(ownerByPlot).filter(o=>o===myAcct).length;
    Object.entries(gs).forEach(([k,ps])=>{
      const i=parseInt(k); if(ownerByPlot[i]!==myAcct) return;
      myPlanted++;
      foodRate+=(FOOD_RATE[ps.species]??0);
    });
    const bhoomRate=myTotal*BHOOMI_RATE_PER_PLOT;
    return {myPlanted,bhoomRate,foodRate};
  },[gs,ownerByPlot,myAcct]);

  const myVacantPlots=useMemo(()=>{
    if(!myAcct) return [] as number[];
    return Object.entries(ownerByPlot)
      .filter(([idx,owner])=>owner===myAcct&&!gs[parseInt(idx)]&&!infraGs[parseInt(idx)])
      .map(([idx])=>parseInt(idx)).sort((a,b)=>a-b);
  },[ownerByPlot,myAcct,gs,infraGs]);

  const handlePlaceInfra = useCallback(async (plotIdx: number, infraType: string) => {
    if (!myAcct) return;
    setPlacingItem(infraType);
    try {
      const r = await fetch('/samatvam/api/samatvam/infrastructure/place', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, plot_index: plotIdx, infrastructure_type: infraType }),
      });
      const d = await r.json();
      if (d.success) {
        setInfraGs(prev => ({ ...prev, [plotIdx]: infraType }));
        setMyInfra(prev => ({ ...prev, placed: { ...prev.placed, [infraType]: (prev.placed[infraType] ?? 0) + 1 } }));
      } else { alert(d.error); }
    } catch { alert('Network error'); }
    setPlacingItem(null);
  }, [myAcct]);

  const handleUnplaceInfra = useCallback(async (plotIdx: number) => {
    if (!myAcct) return;
    try {
      const r = await fetch('/samatvam/api/samatvam/infrastructure/unplace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, plot_index: plotIdx }),
      });
      const d = await r.json();
      if (d.success) {
        const removed = infraGs[plotIdx];
        setInfraGs(prev => { const n = { ...prev }; delete n[plotIdx]; return n; });
        if (removed) setMyInfra(prev => ({ ...prev, placed: { ...prev.placed, [removed]: Math.max(0, (prev.placed[removed] ?? 1) - 1) } }));
      } else { alert(d.error); }
    } catch { alert('Network error'); }
  }, [myAcct, infraGs]);

  const [bhoomAccum,setBhoomAccum]=useState(0);
  const [foodAccum,setFoodAccum]=useState(0);
  const [now,setNow]=useState(Date.now());
  useEffect(()=>{
    if(sel===null) return;
    const t=setInterval(()=>setNow(Date.now()),1000);
    return()=>clearInterval(t);
  },[sel]);
  const tickRef=useRef<number>(Date.now());
  const myPlantedRef=useRef(0);
  const bhoomRateRef=useRef(0); const foodRateRef=useRef(0);
  useEffect(()=>{ myPlantedRef.current=myPlanted; },[myPlanted]);
  useEffect(()=>{ bhoomRateRef.current=bhoomRate; },[bhoomRate]);
  useEffect(()=>{ foodRateRef.current=foodRate; },[foodRate]);

  const [managePlotOpen, setManagePlotOpen] = useState(false);
  const [managePlotCursor, setManagePlotCursor] = useState(0);

  const myAllPlots = useMemo(()=>{
    if(!myAcct) return [] as number[];
    return Object.entries(ownerByPlot).filter(([,o])=>o===myAcct).map(([i])=>parseInt(i)).sort((a,b)=>a-b);
  },[ownerByPlot,myAcct]);

  const [radialMenu,setRadialMenu]=useState<{plotIdx:number;x:number;y:number}|null>(null);
  const [toast,setToast]=useState<{id:number;emoji:string;name:string}|null>(null);
  const toastTimer=useRef<ReturnType<typeof setTimeout>|undefined>(undefined);

  const [harvestAllBusy, setHarvestAllBusy] = useState(false);
  const [harvestAllMsg, setHarvestAllMsg] = useState('');

  const [gpQueueSpecies, setGpQueueSpecies] = useState('neem');
  const [gpQueueQty, setGpQueueQty] = useState(1);
  const [gpQueueSubmitting, setGpQueueSubmitting] = useState(false);
  const [gpQueueMsg, setGpQueueMsg] = useState('');
  const [gpQueueJob, setGpQueueJob] = useState<{ id:number; species:string; quantity:number; planted:number; status:string; error:string|null }|null>(null);

  const [gpBhoomiSpecies, setGpBhoomiSpecies] = useState('amaranth');
  const [gpBhoomiQty, setGpBhoomiQty] = useState(1);
  const [gpBhoomiPlanting, setGpBhoomiPlanting] = useState(false);
  const [gpBhoomiMsg, setGpBhoomiMsg] = useState('');

  const handleHarvestRental = useCallback(async (treeId: number) => {
    if (!myAcct) return;
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/harvest-rental', {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ tree_id: treeId, wax_account: myAcct }),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || 'Harvest failed'); return; }
      setMySeedCount(p => p + 1);
      await fetchRentalTrees();
      clearTimeout(toastTimer.current);
      setToast({ id: Date.now(), emoji: '🌱', name: `+1 seed returned · +${d.logs_gained} ${d.species_name} logs` });
      toastTimer.current = setTimeout(() => setToast(null), 3000);
    } catch { alert('Network error'); }
  }, [myAcct, fetchRentalTrees]);

  const handleBuySeedWax = useCallback(async () => {
    const qty = parseInt(buySeedQty) || 1;
    if (!waxSession || !waxActor) { setBuySeedMsg('Connect your WAX wallet first'); return; }
    setBuySeedStatus('sending'); setBuySeedMsg('');
    try {
      const result = await waxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession.actor, permission: 'active' }],
          data: { from: waxSession.actor, to: 'samatvam.gm', quantity: `${qty}.00000000 WAX`, memo: `season0:seed:${qty}` },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setBuySeedStatus('verifying'); setBuySeedMsg('Transaction sent — checking blockchain…');
      const prevCount = mySeedCount;
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const r = await fetch(`/samatvam/api/samatvam/seeds/${waxActor}`);
          const d = await r.json();
          if (d.success && (d.seeds_in_wallet || 0) > prevCount) {
            setMySeedCount(d.seeds_in_wallet || 0);
            setBuySeedStatus('done');
            setBuySeedMsg(`${qty} seed${qty > 1 ? 's' : ''} added! You now have ${d.seeds_in_wallet} seed${d.seeds_in_wallet !== 1 ? 's' : ''}.`);
            done = true;
          }
        } catch { /* keep polling */ }
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setBuySeedMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setBuySeedStatus('error');
        setBuySeedMsg(`Could not confirm after 60s. Tx ID: ${txId}`);
      }
    } catch (err: any) {
      setBuySeedStatus('error');
      setBuySeedMsg(err.message || 'Transaction failed');
    }
  }, [waxSession, waxActor, buySeedQty, mySeedCount]);

  const [buyError,setBuyError]=useState<string|null>(null);
  const buyErrorTimer=useRef<ReturnType<typeof setTimeout>|undefined>(undefined);

  const [harvesting,setHarvesting]=useState<number|null>(null);
  const [harvestMsg,setHarvestMsg]=useState<string|null>(null);
  const [myLogs,setMyLogs]=useState<Record<number,number>>({});

  const fetchMyLogs=useCallback(async(acct:string)=>{
    try{
      const r=await fetch(`/samatvam/api/samatvam/logs/${acct}`);
      const d=await r.json();
      if(d.success) setMyLogs(d.logs as Record<number,number>);
    }catch{}
  },[]);

  const handleHarvestAll = useCallback(async () => {
    if (!myAcct || harvestAllBusy) return;
    setHarvestAllBusy(true); setHarvestAllMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/harvest-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct }),
      });
      const d = await r.json();
      if (d.success) {
        setHarvestAllMsg(`✅ Harvested ${d.total_trees} trees · +${d.total_logs} logs`);
        fetchMyLogs(myAcct);
        setGs(prev => {
          const n = { ...prev };
          (d.harvested_plots || []).forEach((idx: number) => { delete n[idx]; });
          return n;
        });
      } else {
        setHarvestAllMsg(`❌ ${d.error || 'Harvest failed'}`);
      }
    } catch {
      setHarvestAllMsg('❌ Network error');
    }
    setHarvestAllBusy(false);
    setTimeout(() => setHarvestAllMsg(''), 5000);
  }, [myAcct, harvestAllBusy, fetchMyLogs]);

  const handleGpQueuePlant = useCallback(async () => {
    if (!myAcct || gpQueueSubmitting || gpQueueQty < 1) return;
    setGpQueueSubmitting(true); setGpQueueMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/queue-plant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: gpQueueSpecies, quantity: gpQueueQty }),
      });
      const d = await r.json();
      if (d.success) {
        setGpQueueMsg(`✅ Queued ${gpQueueQty} ${gpQueueSpecies} seeds — ${d.seeds_remaining} seeds remaining`);
      } else {
        setGpQueueMsg(`❌ ${d.error}`);
      }
    } catch { setGpQueueMsg('❌ Network error'); }
    finally { setGpQueueSubmitting(false); }
  }, [myAcct, gpQueueSubmitting, gpQueueQty, gpQueueSpecies]);

  const handleGpBhoomiPlant = useCallback(async (vacantPlots: number[]) => {
    if (!myAcct || gpBhoomiPlanting || gpBhoomiQty < 1) return;
    const si = SPECIES_ID_TO_INDEX[gpBhoomiSpecies];
    if (!si) return;
    const targets = vacantPlots.slice(0, gpBhoomiQty);
    if (targets.length === 0) { setGpBhoomiMsg('❌ No vacant plots to plant on'); return; }
    setGpBhoomiPlanting(true); setGpBhoomiMsg('');
    let planted = 0; let failed = 0;
    for (const plotIdx of targets) {
      try {
        const r = await fetch('/samatvam/api/samatvam/shop/bhoomi-plant', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wax_account: myAcct, plot_index: plotIdx, species_index: si }),
        });
        const d = await r.json();
        if (r.ok && d.success !== false) {
          planted++;
          setGs(prev => ({ ...prev, [plotIdx]: { species: gpBhoomiSpecies, plantedAt: Date.now() / 1000, speciesIndex: si } }));
        } else { failed++; }
      } catch { failed++; }
    }
    setGpBhoomiMsg(`✅ Planted ${planted} ${gpBhoomiSpecies}${failed > 0 ? ` · ${failed} failed` : ''}`);
    setGpBhoomiPlanting(false);
    setTimeout(() => setGpBhoomiMsg(''), 6000);
  }, [myAcct, gpBhoomiPlanting, gpBhoomiQty, gpBhoomiSpecies]);

  // Poll queue job status
  useEffect(() => {
    if (!myAcct) return;
    const poll = () => {
      fetch(`/samatvam/api/samatvam/atavi-forest/queue-status/${myAcct}`)
        .then(r => r.json()).then(d => { if (d.success) setGpQueueJob(d.job); }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [myAcct]);

  const handleHarvest=useCallback(async(plotIdx:number)=>{
    if(!waxActor||harvesting!==null) return;
    setHarvesting(plotIdx);
    setHarvestMsg(null);
    try{
      const r=await fetch('/samatvam/api/samatvam/plot/harvest',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({wax_account:waxActor,plot_index:plotIdx})});
      const d=await r.json();
      if(d.success){
        setHarvestMsg(`🪵 +${d.logs_granted} logs (${d.species_name})`);
        // Clear the tree from local state
        setGs(prev=>{ const n={...prev}; delete n[plotIdx]; return n; });
        // Refresh log inventory
        fetchMyLogs(waxActor);
        setTimeout(()=>setHarvestMsg(null),4000);
      } else {
        setHarvestMsg(`❌ ${d.error||'Harvest failed'}`);
        setTimeout(()=>setHarvestMsg(null),4000);
      }
    }catch{
      setHarvestMsg('❌ Network error');
      setTimeout(()=>setHarvestMsg(null),4000);
    }
    setHarvesting(null);
  },[waxActor,harvesting,fetchMyLogs]);

  const plantSpecies=useCallback(async(idx:number,sp:typeof SPECIES[0])=>{
    const si=SPECIES_ID_TO_INDEX[sp.id]; if(!si) return;

    // If the player owns seeds → use seed path (instant, no Bhoomi cost)
    if(mySeedCount>0){
      const r=await fetch('/samatvam/api/samatvam/plot/plant-seed',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({plot_index:idx,wax_account:waxActor,species_index:si})});
      const data=await r.json();
      if(!r.ok){
        setRadialMenu(null);
        clearTimeout(buyErrorTimer.current);
        setBuyError(data.error||'Plant failed');
        buyErrorTimer.current=setTimeout(()=>setBuyError(null),3000);
        return;
      }
      setMySeedCount(prev=>Math.max(0,prev-1));
      setRadialMenu(null);
      clearTimeout(toastTimer.current);
      setToast({id:Date.now(),emoji:sp.emoji,name:sp.name});
      toastTimer.current=setTimeout(()=>setToast(null),2500);
      fetchRentalTrees();
      return;
    }

    // No seeds — bhoomi path only works on own plots
    if(ownerByPlot[idx]!==waxActor){
      setRadialMenu(null);
      clearTimeout(buyErrorTimer.current);
      setBuyError('Need seeds to plant on others\' land');
      buyErrorTimer.current=setTimeout(()=>setBuyError(null),3000);
      return;
    }
    const cost=SEED_COST[sp.id]??0;
    const r=await fetch('/samatvam/api/samatvam/shop/bhoomi-plant',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({wax_account:waxActor,plot_index:idx,species_index:si})});
    const data=await r.json();
    if(!r.ok){
      setRadialMenu(null);
      clearTimeout(buyErrorTimer.current);
      setBuyError(data.error||'Purchase failed');
      buyErrorTimer.current=setTimeout(()=>setBuyError(null),3000);
      return;
    }
    // Deduct bhoomi locally so UI is instant
    setBhoomAccum(prev=>Math.max(0,prev-cost));
    setGs(prev=>{ const next={...prev}; next[idx]={species:sp.id,plantedAt:Date.now(),speciesIndex:si}; return next; });
    setRadialMenu(null);
    clearTimeout(toastTimer.current);
    setToast({id:Date.now(),emoji:sp.emoji,name:sp.name});
    toastTimer.current=setTimeout(()=>setToast(null),2500);
  },[waxActor,toastTimer,mySeedCount,setMySeedCount,fetchRentalTrees,ownerByPlot]);

  const [buyPlots,setBuyPlots]=useState('1');
  const [buyStatus,setBuyStatus]=useState<'idle'|'sending'|'success'|'error'>('idle');
  const [buyMessage,setBuyMessage]=useState('');

  const handleBuy=useCallback(async()=>{
    if(!waxSession) return;
    const n=parseInt(buyPlots);
    if(!n||n<=0){ setBuyMessage('Enter number of plots'); return; }
    setBuyStatus('sending'); setBuyMessage('');
    try {
      await waxSession.transact({actions:[{account:'eosio.token',name:'transfer',authorization:[{actor:waxSession.actor,permission:'active'}],data:{from:waxSession.actor,to:'samatvam.gm',quantity:`${n.toFixed(8)} WAX`,memo:`season0:land:${n}`}}]});
      setBuyStatus('success'); setBuyMessage(`${n} plot${n>1?'s':''} claimed! Land will appear shortly.`); setBuyPlots('1');
      setTimeout(()=>fetchPlots(),4000);
    } catch(err:any){ setBuyStatus('error'); setBuyMessage(err.message||'Transaction failed'); }
  },[waxSession,buyPlots,fetchPlots]);

  const handleResetCache=useCallback(async()=>{
    if(!window.confirm('Clear all Samatvam browser cache?\n\nYour scores, trees, and land in the database are completely safe.')) return;
    localStorage.clear();
    if(waxSession){ try{await waxDisconnect();}catch{} }
    window.location.reload();
  },[waxSession, waxDisconnect]);

  const [lbRows,setLbRows]=useState<any[]>([]);

  useEffect(()=>{
    fetch('/samatvam/api/samatvam/leaderboard').then(r=>r.json()).then(data=>{
      if(data.success) setLbRows(data.rows);
    }).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!waxActor){ setBhoomAccum(0); setFoodAccum(0); return; }
    tickRef.current=Date.now();
    fetch('/samatvam/api/samatvam/leaderboard').then(r=>r.json()).then(data=>{
      if(!data.success) return;
      const row=(data.rows as any[]).find(r=>r.wax_account===waxActor);
      if(row){
        const bhElapsed=(Date.now()-new Date(row.bhoomi_last_updated).getTime())/3_600_000;
        const bh=parseFloat(row.bhoomi_score)||0,bhR=parseFloat(row.bhoomi_rate)||0;
        setBhoomAccum(bh+bhR*bhElapsed);
        const fdElapsed=(Date.now()-new Date(row.last_updated||row.bhoomi_last_updated).getTime())/3_600_000;
        const fd=parseFloat(row.food_score)||0,fdR=parseFloat(row.food_rate)||0;
        setFoodAccum(fd+fdR*fdElapsed);
        setLbRows(data.rows);
      }
    }).catch(()=>{});
  },[waxActor]);

  useEffect(()=>{ if(waxActor) fetchMyLogs(waxActor); },[waxActor,fetchMyLogs]);

  const bhoomAccumRef=useRef(0); const foodAccumRef=useRef(0);
  useEffect(()=>{ bhoomAccumRef.current=bhoomAccum; },[bhoomAccum]);
  useEffect(()=>{ foodAccumRef.current=foodAccum; },[foodAccum]);

  useEffect(()=>{
    const t=setInterval(()=>{
      if(!waxActor) return;
      const now=Date.now(), elapsed=(now-tickRef.current)/3_600_000;
      tickRef.current=now;
      const bhR=bhoomRateRef.current, fdR=foodRateRef.current;
      const nextBh=bhoomAccumRef.current+bhR*elapsed;
      const nextFd=foodAccumRef.current+fdR*elapsed;
      setBhoomAccum(nextBh);
      setFoodAccum(nextFd);
      // Server owns bhoomi/food — just ping so the row exists
      fetch('/samatvam/api/samatvam/score',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({
        wax_account:waxActor,
      })}).catch(()=>{});
    },60_000);
    return()=>clearInterval(t);
  },[waxActor]);

  const selInfo=useMemo(()=>{
    if(sel===null) return null;
    const coord=allCoords[sel],owner=ownerByPlot[sel]??null,ps=gs[sel]??null;
    const infraType=infraGs[sel]??null;
    const sp=ps?SPECIES.find(s=>s.id===ps.species)??null:null;
    const stage=ps?getStage(ps.species,ps.plantedAt):null;
    const isMine=!!(myAcct&&owner&&owner===myAcct);
    let isMature=false, matureInSec=0;
    if(ps&&ps.speciesIndex){
      const cfg=TREE_HARVEST[ps.speciesIndex];
      if(cfg){ const elapsed=(now-ps.plantedAt)/1000; isMature=elapsed>=cfg.maturity_sec; matureInSec=Math.max(0,cfg.maturity_sec-elapsed); }
    }
    const rentalTrees=gsRental[sel]??[];
    const oldTreeCount=ps?1:0;
    const totalTrees=oldTreeCount+rentalTrees.length;
    const slotsAvailable=5-totalTrees;
    return {coord,owner,ps,sp,stage:stage as 0|1|2|3|null,infraType,isMine,isMature,matureInSec,rentalTrees,totalTrees,slotsAvailable};
  },[sel,allCoords,ownerByPlot,gs,infraGs,myAcct,now,gsRental]);

  // ─── Shared wallet/buy UI blocks ──────────────────────────────────────────
  const WalletBlock=()=>{
    // Connected via WAX
    if(waxActor) return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(74,222,128,0.06)',border:'1px solid rgba(74,222,128,0.2)',borderRadius:10,padding:'8px 12px'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:'#4ade80',boxShadow:'0 0 6px #4ade80',animation:'blink 2s infinite'}}/>
          <span style={{fontFamily:'monospace',color:'#4ade80',fontSize:11,fontWeight:700}}>{waxActor}</span>
          <span style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.08em'}}>WAX</span>
        </div>
        <button onClick={handleWaxDisconnect} style={{display:'flex',alignItems:'center',gap:4,color:'#475569',background:'none',border:'none',cursor:'pointer',fontSize:11}}
          onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color='#f87171';}}
          onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color='#475569';}}
        ><LogOut size={12}/> Disconnect</button>
      </div>
    );
    // Connected via platform profile
    if(profileActor) return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(139,92,246,0.06)',border:'1px solid rgba(139,92,246,0.25)',borderRadius:10,padding:'8px 12px'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:'#a78bfa',boxShadow:'0 0 6px #a78bfa',animation:'blink 2s infinite'}}/>
          <span style={{fontFamily:'monospace',color:'#a78bfa',fontSize:11,fontWeight:700}}>{profileDisplay}</span>
          <span style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.08em'}}>Profile</span>
        </div>
        <button onClick={handleProfileDisconnect} style={{display:'flex',alignItems:'center',gap:4,color:'#475569',background:'none',border:'none',cursor:'pointer',fontSize:11}}
          onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color='#f87171';}}
          onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color='#475569';}}
        ><LogOut size={12}/> Disconnect</button>
      </div>
    );
    // Neutral — waiting for user choice
    return (
      <div>
        <p style={{fontSize:11,color:'#475569',marginBottom:10,lineHeight:1.6}}>Choose how to connect to access your land and tokens.</p>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <button onClick={handleWaxConnect} disabled={waxConnecting}
            style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'9px 12px',borderRadius:10,border:'1px solid rgba(245,158,11,0.3)',background:'rgba(245,158,11,0.08)',color:'#fcd34d',fontSize:12,fontWeight:700,cursor:waxConnecting?'not-allowed':'pointer',opacity:waxConnecting?0.6:1}}>
            {waxConnecting?<><Loader size={13} style={{animation:'spin 1s linear infinite'}}/> Connecting...</>:<><span style={{fontSize:16}}>⬡</span> Connect WAX Wallet</>}
          </button>
          <button onClick={handleProfileConnect}
            style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'9px 12px',borderRadius:10,border:'1px solid rgba(139,92,246,0.3)',background:'rgba(139,92,246,0.08)',color:'#c4b5fd',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            <span style={{fontSize:16}}>👤</span> Connect with Ahantravyuh
          </button>
        </div>
        <p style={{fontSize:10,color:'#334155',marginTop:6,textAlign:'center'}}>WAX: Cloud Wallet &amp; Anchor · Profile: your platform account</p>
        {waxError&&<p style={{fontSize:10,color:'#f87171',marginTop:6}}>{waxError}</p>}
      </div>
    );
  };

  const SEASON0_SOLD_OUT = true; // ← flip to false to re-enable buying

  const BuyBlock=()=>waxSession?(
    <div style={{borderTop:'1px solid rgba(255,255,255,0.06)',paddingTop:10}}>
      {SEASON0_SOLD_OUT?(
        <div>
          {/* Sold out notice */}
          <div style={{textAlign:'center',padding:'14px 10px',borderRadius:10,background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.18)',marginBottom:10}}>
            <div style={{fontSize:22,marginBottom:6}}>🏜️</div>
            <div style={{fontWeight:900,fontSize:13,color:'#fcd34d',marginBottom:4}}>Season 0 — Sold Out</div>
            <div style={{fontSize:11,color:'#64748b',lineHeight:1.6}}>All 10,000 plots have been claimed.<br/>Please wait for <span style={{color:'#f59e0b',fontWeight:700}}>Season 1</span> to acquire new land.</div>
          </div>
          {/* Buy Seeds */}
          <div style={{borderRadius:10,background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.25)',padding:'12px 10px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:900,color:'#c4b5fd',textTransform:'uppercase',letterSpacing:'0.08em'}}>🌱 Buy Seeds</div>
              {mySeedCount>0&&<span style={{fontSize:10,fontWeight:700,color:'#a78bfa',background:'rgba(139,92,246,0.2)',border:'1px solid rgba(139,92,246,0.3)',padding:'2px 8px',borderRadius:20}}>{mySeedCount} seed{mySeedCount!==1?'s':''}</span>}
            </div>
            <div style={{fontSize:10,color:'#475569',marginBottom:8,lineHeight:1.5}}>
              Plant trees on any landowner's plots · 0.8× food rate · 1 WAX per seed
            </div>
            {/* Quick qty picks */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:4,marginBottom:8}}>
              {['1','5','10','25'].map(v=>(
                <button key={v} onClick={()=>{setBuySeedQty(v);setBuySeedStatus('idle');setBuySeedMsg('');}}
                  style={{padding:'5px 0',fontSize:10,fontWeight:700,borderRadius:7,border:`1px solid ${buySeedQty===v?'rgba(139,92,246,0.5)':'rgba(255,255,255,0.08)'}`,background:buySeedQty===v?'rgba(139,92,246,0.2)':'rgba(255,255,255,0.04)',color:buySeedQty===v?'#c4b5fd':'#475569',cursor:'pointer'}}
                >{v}</button>
              ))}
            </div>
            <button onClick={handleBuySeedWax}
              disabled={buySeedStatus==='sending'||buySeedStatus==='verifying'}
              style={{width:'100%',padding:'9px',borderRadius:10,background:'linear-gradient(135deg,#7c3aed,#6d28d9)',color:'white',fontSize:12,fontWeight:900,border:'none',cursor:buySeedStatus==='sending'||buySeedStatus==='verifying'?'not-allowed':'pointer',opacity:buySeedStatus==='sending'||buySeedStatus==='verifying'?0.6:1}}
            >
              {buySeedStatus==='sending'?'Sending transaction…':buySeedStatus==='verifying'?'Verifying on blockchain…':`Buy ${parseInt(buySeedQty)||1} Seed${(parseInt(buySeedQty)||1)>1?'s':''} · ${parseInt(buySeedQty)||1} WAX`}
            </button>
            {buySeedMsg&&<p style={{fontSize:10,marginTop:6,color:buySeedStatus==='error'?'#f87171':buySeedStatus==='done'?'#86efac':'#a78bfa',textAlign:'center'}}>{buySeedMsg}</p>}
          </div>
        </div>
      ):(
        /* ── BUY UI — keep intact, controlled by SEASON0_SOLD_OUT flag ── */
        <>
          <p style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:8}}>Buy More Plots</p>
          <div style={{position:'relative',marginBottom:8}}>
            <input type="number" value={buyPlots} min="1" placeholder="1" onChange={e=>{setBuyPlots(e.target.value);setBuyStatus('idle');setBuyMessage('');}}
              style={{width:'100%',padding:'8px 70px 8px 10px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:9,color:'white',fontSize:12,outline:'none',boxSizing:'border-box'}}/>
            <span style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:11,color:'#f59e0b',fontWeight:700,pointerEvents:'none'}}>= {parseInt(buyPlots)||0} WAX</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:4,marginBottom:8}}>
            {['1','5','10','50'].map(v=>(
              <button key={v} onClick={()=>{setBuyPlots(v);setBuyStatus('idle');setBuyMessage('');}}
                style={{padding:'5px 0',fontSize:10,fontWeight:700,borderRadius:7,border:`1px solid ${buyPlots===v?'rgba(245,158,11,0.5)':'rgba(255,255,255,0.08)'}`,background:buyPlots===v?'rgba(245,158,11,0.15)':'rgba(255,255,255,0.04)',color:buyPlots===v?'#fcd34d':'#475569',cursor:'pointer'}}
              >{v}</button>
            ))}
          </div>
          <button onClick={handleBuy} disabled={buyStatus==='sending'||!parseInt(buyPlots)}
            style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px',borderRadius:10,background:'linear-gradient(135deg,#f59e0b,#ea580c)',color:'white',fontSize:12,fontWeight:900,border:'none',cursor:buyStatus==='sending'?'not-allowed':'pointer',opacity:buyStatus==='sending'||!parseInt(buyPlots)?0.6:1}}
          >
            {buyStatus==='sending'?<><Loader size={13} style={{animation:'spin 1s linear infinite'}}/> Claiming...</>:<>🏜️ Claim {parseInt(buyPlots)||0} Plot{(parseInt(buyPlots)||0)!==1?'s':''}</>}
          </button>
          {buyMessage&&<p style={{fontSize:10,marginTop:6,color:buyStatus==='success'?'#4ade80':'#f87171',lineHeight:1.5}}>{buyMessage}</p>}
        </>
      )}
    </div>
  ):null;

  // ─── JSX ──────────────────────────────────────────────────────────────────
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh',background:'#020617',color:'white',overflow:'hidden',fontFamily:'system-ui,sans-serif'}}>

      {/* Top bar */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',background:'rgba(15,23,42,0.95)',borderBottom:'1px solid rgba(255,255,255,0.07)',flexShrink:0,zIndex:20}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button onClick={onBack} style={{display:'flex',alignItems:'center',gap:6,color:'#94a3b8',background:'none',border:'none',cursor:'pointer',fontSize:13,fontWeight:600}}>
            <ArrowLeft size={16}/> Dashboard
          </button>
          <div style={{width:1,height:20,background:'rgba(255,255,255,0.1)'}}/>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:28,height:28,background:'rgba(16,185,129,0.15)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>🌳</div>
            {!isMobile&&<span style={{fontWeight:900,fontSize:15}}>Atavi Forest</span>}
            {!isMobile&&<span style={{fontSize:9,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.25)',color:'#fcd34d',padding:'2px 8px',borderRadius:999}}>Season 0 · Phase 0</span>}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:isMobile?10:24}}>
          {/* Desktop stats */}
          {!isMobile&&<>{[{v:totalOwned,l:'Owned',c:'#f59e0b'},{v:TOTAL_PLOTS-totalOwned,l:'Free',c:'#475569'},{v:planted,l:'Planted',c:'#4ade80'}].map(s=>(
            <div key={s.l} style={{textAlign:'center'}}>
              <div style={{fontWeight:900,fontSize:15,color:s.c,lineHeight:1}}>{s.v}</div>
              <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#334155',marginTop:2}}>{s.l}</div>
            </div>
          ))}
          <div style={{width:1,height:28,background:'rgba(255,255,255,0.07)'}}/>
          <div style={{textAlign:'center'}}>
            <div style={{fontWeight:900,fontSize:14,color:'#d97706',lineHeight:1}}>🪙 {bhoomAccum.toFixed(1)}</div>
            <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#334155',marginTop:2}}>Bhoomi</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontWeight:900,fontSize:14,color:'#f472b6',lineHeight:1}}>🍎 {foodAccum.toFixed(1)}</div>
            <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#334155',marginTop:2}}>Food</div>
          </div>
          {bhoomRate>0&&<div style={{textAlign:'center'}}><div style={{fontSize:10,color:'#475569',lineHeight:1.5,fontFamily:'monospace'}}>+{bhoomRate.toFixed(1)} bh/hr<br/>+{foodRate.toFixed(1)} fd/hr</div></div>}</>}
          {myAcct&&<><div style={{width:1,height:28,background:'rgba(255,255,255,0.07)'}}/>
          <div style={{textAlign:'center',cursor:'pointer'}} title="Your seeds">
            <div style={{fontWeight:900,fontSize:14,color:'#a78bfa',lineHeight:1}}>🌱 {mySeedCount}</div>
            <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#334155',marginTop:2}}>Seeds</div>
          </div></>}
          {/* Mobile: wallet dot — hidden to save nav space */}
          {/* Lite mode toggle */}
          <button
            onClick={()=>setLiteMode(v=>!v)}
            title={liteMode?'Switch to Full mode (effects on)':'Switch to Lite mode (effects off)'}
            style={{display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:8,border:`1px solid ${liteMode?'rgba(16,185,129,0.4)':'rgba(255,255,255,0.1)'}`,background:liteMode?'rgba(16,185,129,0.1)':'rgba(255,255,255,0.04)',color:liteMode?'#34d399':'#64748b',fontSize:11,fontWeight:700,cursor:'pointer',transition:'all 0.2s'}}
          >{liteMode?'⚡ LITE':'✨ FULL'}</button>
          <button onClick={onLeaderboard} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:8,border:'1px solid rgba(245,158,11,0.25)',background:'rgba(245,158,11,0.06)',color:'#fcd34d',fontSize:11,fontWeight:700,cursor:'pointer'}}
            onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.14)';}}
            onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.06)';}}
          ><Trophy size={13}/>{!isMobile&&' Leaderboard'}</button>
          {!isMobile&&<div style={{display:'flex',alignItems:'center',gap:4}}>
            <button onClick={()=>setZoom(z=>Math.max(0.3,z-0.2))} style={{width:24,height:24,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:6,color:'#94a3b8',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>−</button>
            <span style={{width:42,textAlign:'center',fontSize:11,color:'#64748b'}}>{Math.round(zoom*100)}%</span>
            <button onClick={()=>setZoom(z=>Math.min(4,z+0.2))} style={{width:24,height:24,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:6,color:'#94a3b8',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
            <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} style={{fontSize:10,color:'#475569',background:'none',border:'none',cursor:'pointer',marginLeft:4}}>Reset</button>
          </div>}
        </div>
      </div>

      {/* Body */}
      <div style={{display:'flex',flex:1,overflow:'hidden',flexDirection:isMobile?'column':'row'}}>

        {/* ── Desktop: Left panel ── */}
        {!isMobile&&(
          <div style={{width:240,background:'rgba(15,23,42,0.95)',borderRight:'1px solid rgba(255,255,255,0.07)',display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
            <div style={{flex:1,overflowY:'auto',padding:14}}>
              <StepCard number={1} title="Connect WAX Wallet" done={!!waxActor}><WalletBlock/></StepCard>
              <StepCard number={2} title="Your Plots" done={!!(myAcct&&acctCount[myAcct])} locked={!waxActor}>
                {myAcct&&acctCount[myAcct]?(
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                      <div style={{width:10,height:10,borderRadius:'50%',background:acctColor[myAcct]||'#f59e0b',flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:700,color:'white'}}>You own <span style={{color:'#f59e0b'}}>{acctCount[myAcct]}</span> plot{acctCount[myAcct]>1?'s':''}</span>
                    </div>
                    <div style={{fontSize:10,color:'#475569',marginBottom:10,fontFamily:'monospace',lineHeight:1.6}}>
                      #{Object.entries(ownerByPlot).filter(([,o])=>o===myAcct).map(([i])=>parseInt(i)+1).sort((a,b)=>a-b).join(', ')}
                    </div>
                    <button onClick={()=>{setManagePlotCursor(0);setManagePlotOpen(true);}}
                      style={{width:'100%',padding:'8px 10px',borderRadius:10,border:'1px solid rgba(245,158,11,0.3)',background:'rgba(245,158,11,0.07)',color:'#fcd34d',fontSize:11,fontWeight:700,cursor:'pointer',marginBottom:8}}
                      onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.14)';}}
                      onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.07)';}}
                    >🌿 Manage Your Plots</button>
                    <button onClick={handleHarvestAll} disabled={ATAVI_LOCKED || harvestAllBusy}
                      style={{width:'100%',padding:'8px 10px',borderRadius:10,border:'1px solid rgba(74,222,128,0.3)',background:'rgba(74,222,128,0.07)',color:'#4ade80',fontSize:11,fontWeight:700,cursor:(ATAVI_LOCKED||harvestAllBusy)?'not-allowed':'pointer',marginBottom:4,opacity:(ATAVI_LOCKED||harvestAllBusy)?0.6:1}}
                    >{ATAVI_LOCKED ? '🔒 Harvest paused' : harvestAllBusy ? '⏳ Harvesting…' : '🪓 Harvest All Mature'}</button>
                    {harvestAllMsg&&<p style={{fontSize:10,color:harvestAllMsg.startsWith('✅')?'#4ade80':'#f87171',marginTop:4,lineHeight:1.5}}>{harvestAllMsg}</p>}
                  </div>
                ):(
                  <p style={{fontSize:11,color:'#475569',marginBottom:10,lineHeight:1.6}}>No plots yet. Buy below to claim land.</p>
                )}
                <BuyBlock/>
              </StepCard>
              <div style={{marginTop:4,padding:'12px',borderRadius:10,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',textAlign:'center'}}>
                <div style={{fontSize:11,color:'#475569',fontWeight:700}}>🚫 Planting closed — use Atavi Forest for new trees</div>
              </div>
            </div>
          </div>
        )}

        {/* ── Mobile: Two 50/50 panels ── */}
        {isMobile&&(
          <div style={{display:'flex',flex:1,overflow:'hidden'}}>

            {/* Mobile Left 50% — Wallet + Plots */}
            <div style={{width:'50%',overflowY:'auto',padding:'12px 10px',background:'rgba(15,23,42,0.95)',borderRight:'1px solid rgba(255,255,255,0.06)',flexShrink:0}}>
              {myAcct&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:12}}>
                  {[{emoji:'🪙',val:bhoomAccum,rate:bhoomRate,label:'Bhoomi',color:'#d97706'},{emoji:'🍎',val:foodAccum,rate:foodRate,label:'Food',color:'#f472b6'}].map(t=>(
                    <div key={t.label} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'8px',textAlign:'center'}}>
                      <div style={{fontSize:16,lineHeight:1}}>{t.emoji}</div>
                      <div style={{fontWeight:900,fontSize:17,color:t.color,lineHeight:1.1,marginTop:3}}>{t.val.toFixed(1)}</div>
                      <div style={{fontSize:8,color:'#475569',textTransform:'uppercase',letterSpacing:'0.07em',marginTop:2}}>{t.label}</div>
                      {t.rate>0&&<div style={{fontSize:8,color:t.color,fontWeight:700,marginTop:1}}>+{t.rate.toFixed(2)}/hr</div>}
                    </div>
                  ))}
                </div>
              )}
              <StepCard number={1} title="Connect WAX Wallet" done={!!waxActor}><WalletBlock/></StepCard>
              <StepCard number={2} title="Your Plots" done={!!(myAcct&&acctCount[myAcct])} locked={!waxActor}>
                {myAcct&&acctCount[myAcct]?(
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                      <div style={{width:10,height:10,borderRadius:'50%',background:acctColor[myAcct]||'#f59e0b',flexShrink:0}}/>
                      <span style={{fontSize:12,fontWeight:700,color:'white'}}>You own <span style={{color:'#f59e0b'}}>{acctCount[myAcct]}</span> plot{acctCount[myAcct]>1?'s':''}</span>
                    </div>
                    <button onClick={()=>{setManagePlotCursor(0);setManagePlotOpen(true);}}
                      style={{width:'100%',padding:'8px 10px',borderRadius:10,border:'1px solid rgba(245,158,11,0.3)',background:'rgba(245,158,11,0.07)',color:'#fcd34d',fontSize:11,fontWeight:700,cursor:'pointer',marginBottom:8}}
                      onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.14)';}}
                      onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.07)';}}
                    >🌿 Manage Your Plots</button>
                    <button onClick={handleHarvestAll} disabled={ATAVI_LOCKED || harvestAllBusy}
                      style={{width:'100%',padding:'8px 10px',borderRadius:10,border:'1px solid rgba(74,222,128,0.3)',background:'rgba(74,222,128,0.07)',color:'#4ade80',fontSize:11,fontWeight:700,cursor:(ATAVI_LOCKED||harvestAllBusy)?'not-allowed':'pointer',marginBottom:4,opacity:(ATAVI_LOCKED||harvestAllBusy)?0.6:1}}
                    >{ATAVI_LOCKED ? '🔒 Harvest paused' : harvestAllBusy ? '⏳ Harvesting…' : '🪓 Harvest All Mature'}</button>
                    {harvestAllMsg&&<p style={{fontSize:10,color:harvestAllMsg.startsWith('✅')?'#4ade80':'#f87171',marginTop:4,lineHeight:1.5}}>{harvestAllMsg}</p>}
                  </div>
                ):(<p style={{fontSize:11,color:'#475569',marginBottom:10,lineHeight:1.6}}>No plots yet. Buy below.</p>)}
                <BuyBlock/>
              </StepCard>
            </div>

            {/* Mobile Right 50% — Tokens + Colony + collapsible Plant list */}
            <div style={{width:'50%',overflowY:'auto',padding:'12px 10px',background:'rgba(15,23,42,0.95)',flexShrink:0}}>

              {/* Colony Tokens */}
              <StepCard number={4} title="Colony Tokens" done={bhoomAccum>0||foodAccum>0} locked={!waxActor}>
                <div>
                  {[
                    {label:'Bhoomi',emoji:'🪙',val:bhoomAccum,rate:bhoomRate,color:'#d97706',sub:'from land'},
                    {label:'Food',emoji:'🍎',val:foodAccum,rate:foodRate,color:'#f472b6',sub:'from trees'},
                  ].map(t=>(
                    <div key={t.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <div>
                        <div style={{fontSize:11,color:'#64748b',fontWeight:600}}>{t.emoji} {t.label} <span style={{fontSize:9,color:'#334155',fontWeight:400}}>{t.sub}</span></div>
                        {t.rate>0&&<div style={{fontSize:9,color:t.color,fontWeight:700,marginTop:2}}>+{t.rate.toFixed(2)}/hr</div>}
                      </div>
                      <span style={{fontSize:20,fontWeight:900,color:t.color,fontFamily:'monospace'}}>{t.val.toFixed(1)}</span>
                    </div>
                  ))}
                  {waxActor&&bhoomRate===0&&<p style={{fontSize:10,color:'#334155',marginTop:8}}>Own land to start earning Bhoomi</p>}
                  {waxActor&&Object.values(myLogs).some(v=>v>0)&&(
                    <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(255,255,255,0.04)'}}>
                      <p style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:6}}>🪵 Logs</p>
                      <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                        {Object.entries(myLogs).filter(([,v])=>v>0).map(([si,qty])=>{
                          const cfg=TREE_HARVEST[parseInt(si)];
                          return cfg?(
                            <div key={si} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,background:'rgba(134,239,172,0.06)',border:'1px solid rgba(134,239,172,0.15)',borderRadius:6,padding:'3px 7px',color:'#86efac'}}>
                              <span style={{fontWeight:700}}>{qty}×</span><span style={{color:'#64748b'}}>{cfg.name}</span>
                            </div>
                          ):null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </StepCard>

              {/* Colony stats */}
              <StepCard number={5} title="Colony" done={false} locked={false}>
                <div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,marginBottom:10}}>
                    {[{v:totalOwned,l:'Owned',c:'#f59e0b'},{v:planted,l:'Planted',c:'#4ade80'},{v:Object.keys(acctCount).length,l:'Owners',c:'#93c5fd'}].map(s=>(
                      <div key={s.l} style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'8px 6px',textAlign:'center'}}>
                        <div style={{fontWeight:900,fontSize:15,color:s.c,lineHeight:1}}>{s.v}</div>
                        <div style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.08em',marginTop:3}}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={onLeaderboard} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 12px',borderRadius:10,border:'1px solid rgba(245,158,11,0.25)',background:'rgba(245,158,11,0.06)',color:'#fcd34d',fontSize:12,fontWeight:700,cursor:'pointer'}}
                  ><Trophy size={13}/> Leaderboard</button>
                </div>
              </StepCard>

              {/* Plant Species — collapsible */}
              {(waxActor&&acctCount[myAcct]>0)&&(
                <div style={{marginBottom:14,borderRadius:12,border:`1px solid ${myPlanted>0?'rgba(74,222,128,0.2)':'rgba(255,255,255,0.09)'}`,background:'rgba(15,23,42,0.5)',overflow:'hidden'}}>
                  <button onClick={()=>setMobilePlantOpen(v=>!v)}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'9px 14px',borderBottom:mobilePlantOpen?'1px solid rgba(255,255,255,0.05)':'none',background:'transparent',border:'none',cursor:'pointer',textAlign:'left'}}
                  >
                    <div style={{width:22,height:22,borderRadius:'50%',flexShrink:0,background:myPlanted>0?'#4ade80':'rgba(245,158,11,0.12)',border:myPlanted>0?'none':'1px solid rgba(245,158,11,0.25)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:myPlanted>0?'#020617':'#fcd34d'}}>
                      {myPlanted>0?'✓':3}
                    </div>
                    <span style={{fontSize:12,fontWeight:700,color:myPlanted>0?'#4ade80':'white',flex:1}}>Plant Species</span>
                    <span style={{fontSize:10,color:'#475569',flexShrink:0}}>{myPlanted}/{acctCount[myAcct]} · {mobilePlantOpen?'▲':'▼'}</span>
                  </button>
                  {mobilePlantOpen&&(
                    <div style={{padding:'12px 14px'}}>
                      {myVacantPlots.length>0?(
                        <div style={{padding:'10px',borderRadius:8,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',textAlign:'center'}}>
                          <p style={{fontSize:11,color:'#475569',fontWeight:700}}>🚫 Planting closed — use Atavi Forest</p>
                        </div>
                      ):<p style={{fontSize:11,color:'#4ade80',fontWeight:600}}>🌿 All plots planted!</p>}
                    </div>
                  )}
                </div>
              )}

              <div style={{marginTop:8,padding:'10px',borderRadius:10,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',textAlign:'center'}}>
                <div style={{fontSize:10,color:'#475569',fontWeight:700}}>🚫 Planting closed — use Atavi Forest for new trees</div>
              </div>

              <div style={{marginTop:8,paddingTop:12,borderTop:'1px solid rgba(255,255,255,0.04)'}}>
                <button onClick={handleResetCache}
                  style={{width:'100%',padding:'7px',borderRadius:9,border:'1px solid rgba(239,68,68,0.12)',background:'rgba(239,68,68,0.04)',color:'rgba(239,68,68,0.5)',fontSize:10,fontWeight:700,cursor:'pointer'}}
                >↺ Reset Cache</button>
              </div>
            </div>

          </div>
        )}

        {/* ── Mobile: Show / Hide Map button ── */}
        {isMobile&&(
          <button
            onClick={()=>setShowMobileMap(v=>!v)}
            style={{flexShrink:0,width:'100%',padding:'10px 16px',background:'rgba(8,14,28,0.98)',borderTop:'1px solid rgba(255,255,255,0.08)',borderBottom:showMobileMap?'1px solid rgba(255,255,255,0.08)':'none',color:showMobileMap?'#4ade80':'#64748b',fontSize:12,fontWeight:700,cursor:'pointer',border:'none',display:'flex',alignItems:'center',justifyContent:'center',gap:8,letterSpacing:'0.05em'}}
          >
            <span style={{fontSize:16}}>🗺️</span>
            {showMobileMap?'Hide Map ▲':'Show Map ▼'}
          </button>
        )}

        {/* ── Canvas map (always in DOM; hidden on mobile until showMobileMap) ── */}
        <div
          ref={wrapRef}
          style={{flex:1,overflow:'hidden',position:'relative',cursor:drag.current.active?'grabbing':'grab',minHeight:isMobile?'60vw':undefined,display:(isMobile&&!showMobileMap)?'none':'flex'}}
        >
          <canvas
            ref={canvasRef}
            style={{display:'block',width:'100%',height:'100%',touchAction:'none'}}
            onMouseDown={onMD}
            onMouseMove={onMM}
            onMouseUp={onMU}
            onMouseLeave={()=>{drag.current.active=false;}}
            onWheel={onWh}
            onTouchStart={onTE}
            onTouchMove={onTM}
            onTouchEnd={onTEnd}
          />

          {/* Buy error toast */}
          {buyError&&(
            <div style={{position:'absolute',bottom:56,left:'50%',transform:'translateX(-50%)',background:'rgba(15,23,42,0.96)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:12,padding:'9px 18px',display:'flex',alignItems:'center',gap:8,pointerEvents:'none',zIndex:30,whiteSpace:'nowrap'}}>
              <span style={{fontSize:14}}>🪙</span>
              <span style={{fontSize:12,fontWeight:700,color:'#f87171'}}>{buyError}</span>
            </div>
          )}

          {/* Toast */}
          {toast&&(
            <div key={toast.id} style={{position:'absolute',bottom:56,left:'50%',transform:'translateX(-50%)',background:'rgba(15,23,42,0.96)',border:'1px solid rgba(74,222,128,0.35)',borderRadius:12,padding:'9px 18px',display:'flex',alignItems:'center',gap:8,pointerEvents:'none',zIndex:30,animation:'toastIn 0.25s ease',boxShadow:'0 4px 20px rgba(0,0,0,0.5)',whiteSpace:'nowrap'}}>
              <span style={{fontSize:18}}>{toast.emoji}</span>
              <span style={{fontSize:13,fontWeight:700,color:'#4ade80'}}>{toast.name} planted!</span>
            </div>
          )}

          {/* Legend */}
          <div style={{position:'absolute',bottom:16,left:16,display:'flex',gap:12,flexWrap:'wrap',pointerEvents:'none'}}>
            {[
              {color:'rgba(245,158,11,0.3)',border:'rgba(245,158,11,0.6)',label:'Owned'},
              {color:'rgba(255,255,255,0.05)',border:'rgba(255,255,255,0.1)',label:'Available'},
              {color:'rgba(14,116,144,0.7)',border:'rgba(6,182,212,0.8)',label:'💧 Harvester'},
              {color:'rgba(194,65,12,0.7)',border:'rgba(249,115,22,0.8)',label:'🛒 Hand Cart'},
            ].map(l=>(
              <div key={l.label} style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'#64748b'}}>
                <div style={{width:12,height:12,borderRadius:2,background:l.color,border:`1px solid ${l.border}`}}/>{l.label}
              </div>
            ))}
          </div>
          <div style={{position:'absolute',top:16,left:'50%',transform:'translateX(-50%)',fontSize:11,color:'#1e293b',pointerEvents:'none'}}>Drag to pan · Scroll to zoom · Click a plot</div>
          {plotsLoading&&<div style={{position:'absolute',top:16,right:16,fontSize:11,color:'#334155',pointerEvents:'none'}}>Loading…</div>}
          {!plotsLoading&&myAcct&&acctCount[myAcct]>0&&(
            <div style={{position:'absolute',top:14,right:16,display:'flex',gap:6,zIndex:10}}>
              <button onClick={()=>{ setShowMyPlotsOnly(v=>!v); setShowHarvestableOnly(false); }}
                style={{padding:'5px 12px',borderRadius:8,border:`1px solid ${showMyPlotsOnly?'rgba(245,158,11,0.7)':'rgba(255,255,255,0.12)'}`,background:showMyPlotsOnly?'rgba(245,158,11,0.18)':'rgba(15,23,42,0.82)',color:showMyPlotsOnly?'#fcd34d':'#64748b',fontSize:11,fontWeight:700,cursor:'pointer',backdropFilter:'blur(4px)',transition:'all 0.15s',letterSpacing:'0.03em'}}
              >{showMyPlotsOnly?'🟡 My Plots':'◻ My Plots'}</button>
              <button onClick={()=>{ setShowHarvestableOnly(v=>!v); setShowMyPlotsOnly(false); }}
                style={{padding:'5px 12px',borderRadius:8,border:`1px solid ${showHarvestableOnly?'rgba(134,239,172,0.7)':'rgba(255,255,255,0.12)'}`,background:showHarvestableOnly?'rgba(134,239,172,0.12)':'rgba(15,23,42,0.82)',color:showHarvestableOnly?'#86efac':'#64748b',fontSize:11,fontWeight:700,cursor:'pointer',backdropFilter:'blur(4px)',transition:'all 0.15s',letterSpacing:'0.03em'}}
              >{showHarvestableOnly?'🪵 Harvestable':'◻ Harvestable'}</button>
            </div>
          )}
        </div>

        {/* ── Desktop: Right panel ── */}
        {!isMobile&&(
        <div style={{width:260,display:'flex',flexDirection:'column',background:'rgba(15,23,42,0.95)',borderLeft:'1px solid rgba(255,255,255,0.07)',overflow:'hidden',flexShrink:0}}>
          <div style={{flex:1,overflowY:'auto',padding:16}}>

            {/* Step 3 */}
            <StepCard number={3} title="Plant Species" done={myPlanted>0} locked={!waxActor||!acctCount[myAcct]}>
              <div>
                {acctCount[myAcct]>0&&(
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                    <span style={{fontSize:11,color:'#475569'}}>{myPlanted} of {acctCount[myAcct]} plots planted</span>
                    <span style={{fontSize:11,color:myPlanted>0?'#4ade80':'#334155',fontWeight:700}}>{Math.round(myPlanted/(acctCount[myAcct]||1)*100)}%</span>
                  </div>
                )}
                {myVacantPlots.length>0?(
                    <div style={{background:'rgba(100,116,139,0.05)',border:'1px solid rgba(100,116,139,0.2)',borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
                      <p style={{fontSize:11,color:'#475569',fontWeight:700}}>🚫 Planting closed — use Atavi Forest</p>
                    </div>
                  ):myPlanted>0?<p style={{fontSize:11,color:'#4ade80',fontWeight:600}}>🌿 All your plots are planted!</p>:null}
                {sel!==null&&selInfo&&(
                  <div style={{marginTop:12,background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                      <span style={{fontSize:12,fontWeight:700,color:'white'}}>Plot #{sel+1}</span>
                      <button onClick={()=>setSel(null)} style={{color:'#334155',background:'none',border:'none',cursor:'pointer',fontSize:12}}>✕</button>
                    </div>
                    <div style={{fontFamily:'monospace',fontSize:10,color:'#475569',marginBottom:8}}>({selInfo.coord[0]}, {selInfo.coord[1]}) · {selInfo.owner||'Available'}</div>
                    {selInfo.ps&&selInfo.sp&&selInfo.stage!==null&&(
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                        <span style={{fontSize:18}}>{selInfo.sp.emoji}</span>
                        <div>
                          <div style={{color:'white',fontSize:11,fontWeight:700}}>{selInfo.sp.name}</div>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <div style={{width:6,height:6,borderRadius:'50%',background:STAGE_COLORS[selInfo.stage]}}/>
                            <span style={{fontSize:10,color:STAGE_COLORS[selInfo.stage]}}>{STAGE_NAMES[selInfo.stage]}</span>
                            <span style={{fontSize:10,color:'#f472b6'}}>· 🍎+{FOOD_RATE[selInfo.sp.id]??0}/hr</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {selInfo.infraType&&(
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 8px',borderRadius:8,background:'rgba(6,182,212,0.08)',border:'1px solid rgba(6,182,212,0.2)'}}>
                        <span style={{fontSize:18}}>{INFRA_PAL[selInfo.infraType]?.emoji??'🏗️'}</span>
                        <div>
                          <div style={{color:'white',fontSize:11,fontWeight:700}}>{selInfo.infraType==='water_harvester'?'Water Harvester':'Hand Cart'}</div>
                          <div style={{fontSize:9,color:'#64748b'}}>{selInfo.infraType==='water_harvester'?'+10% food rate · active':'Transport enabled · active'}</div>
                        </div>
                      </div>
                    )}
                    {selInfo.isMine&&selInfo.ps&&!selInfo.infraType&&(
                      <>
                        {!selInfo.isMature&&(
                          <div style={{width:'100%',padding:'5px 6px',borderRadius:8,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',color:'#94a3b8',fontSize:9,marginBottom:4,textAlign:'center'}}>
                            🕐 Mature in {fmtDuration(selInfo.matureInSec)}
                          </div>
                        )}
                        {selInfo.isMature&&(
                          <button onClick={()=>handleHarvest(sel!)} disabled={harvesting===sel}
                            style={{width:'100%',padding:'6px',borderRadius:8,border:'1px solid rgba(134,239,172,0.3)',background:'rgba(134,239,172,0.08)',color:'#86efac',fontSize:10,cursor:harvesting===sel?'not-allowed':'pointer',marginBottom:4,fontWeight:700,opacity:harvesting===sel?0.6:1}}
                          >{harvesting===sel?'Harvesting…':'🪵 Harvest Tree'}</button>
                        )}
                        {harvestMsg&&harvesting===null&&(
                          <div style={{fontSize:10,color:'#86efac',marginBottom:4,textAlign:'center'}}>{harvestMsg}</div>
                        )}
                        <button onClick={()=>setPlot(sel,null,waxActor)}
                          style={{width:'100%',padding:'6px',borderRadius:8,border:'1px solid rgba(239,68,68,0.15)',background:'rgba(239,68,68,0.05)',color:'rgba(239,68,68,0.6)',fontSize:10,cursor:'pointer'}}
                          onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color='#f87171';}}
                          onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color='rgba(239,68,68,0.6)';}}
                        >Remove plant</button>
                      </>
                    )}
                    {selInfo.isMine&&selInfo.infraType&&(
                      <button onClick={()=>handleUnplaceInfra(sel)}
                        style={{width:'100%',padding:'6px',borderRadius:8,border:'1px solid rgba(239,68,68,0.15)',background:'rgba(239,68,68,0.05)',color:'rgba(239,68,68,0.6)',fontSize:10,cursor:'pointer'}}
                        onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color='#f87171';}}
                        onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color='rgba(239,68,68,0.6)';}}
                      >Remove {selInfo.infraType==='water_harvester'?'Water Harvester':'Hand Cart'}</button>
                    )}
                    {selInfo.isMine&&!selInfo.ps&&!selInfo.infraType&&(myInfra.machine_owned||myInfra.handcart_owned)&&(
                      <p style={{fontSize:10,color:'#475569',marginTop:4,fontStyle:'italic'}}>Click plot again to open the placement wheel</p>
                    )}

                    {/* ── Rental trees on this plot ── */}
                    {selInfo.rentalTrees.length>0&&(
                      <div style={{marginTop:8,borderTop:'1px solid rgba(139,92,246,0.15)',paddingTop:8}}>
                        <p style={{fontSize:9,color:'#7c3aed',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:6}}>🌱 Rental Trees ({selInfo.rentalTrees.length})</p>
                        {selInfo.rentalTrees.map(rt=>{
                          const rtSp=SPECIES.find(s=>s.id===rt.species)??null;
                          const rtCfg=TREE_HARVEST[rt.speciesIndex];
                          const rtElapsed=rtCfg?(Date.now()-rt.plantedAt)/1000:0;
                          const rtMature=rtCfg?rtElapsed>=rtCfg.maturity_sec:false;
                          const rtMine=rt.plantedBy===myAcct;
                          return (
                            <div key={rt.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <span style={{fontSize:13}}>{rtSp?.emoji??'🌱'}</span>
                                <div>
                                  <div style={{fontSize:10,color:'#c4b5fd',fontWeight:700}}>{rtSp?.name??'Unknown'}</div>
                                  <div style={{fontSize:9,color:'#475569',fontFamily:'monospace'}}>{rt.plantedBy}</div>
                                </div>
                              </div>
                              {rtMine&&rtMature?(
                                <button onClick={()=>handleHarvestRental(rt.id)}
                                  style={{padding:'3px 8px',borderRadius:6,border:'1px solid rgba(134,239,172,0.3)',background:'rgba(134,239,172,0.08)',color:'#86efac',fontSize:9,cursor:'pointer',fontWeight:700}}
                                >🪵 Harvest</button>
                              ):rtMine?(
                                <span style={{fontSize:9,color:'#475569'}}>🕐 {fmtDuration(rtCfg?Math.max(0,rtCfg.maturity_sec-rtElapsed):0)}</span>
                              ):(
                                <span style={{fontSize:9,color:'#334155'}}>{rtMature?'Mature':'Growing'}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selInfo.owner&&myAcct&&selInfo.slotsAvailable===0&&(
                      <div style={{fontSize:9,color:'#475569',marginTop:6,textAlign:'center'}}>Plot full (5/5 trees)</div>
                    )}
                  </div>
                )}
              </div>
            </StepCard>


            {/* Step 4 */}
            <StepCard number={4} title="Colony Tokens" done={bhoomAccum>0||foodAccum>0} locked={!waxActor}>
              <div>
                {[
                  {label:'Bhoomi',emoji:'🪙',val:bhoomAccum,rate:bhoomRate,color:'#d97706',sub:'from land'},
                  {label:'Food',emoji:'🍎',val:foodAccum,rate:foodRate,color:'#f472b6',sub:'from trees'},
                ].map(t=>(
                  <div key={t.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                    <div>
                      <div style={{fontSize:11,color:'#64748b',fontWeight:600}}>{t.emoji} {t.label} <span style={{fontSize:9,color:'#334155',fontWeight:400}}>{t.sub}</span></div>
                      {t.rate>0&&<div style={{fontSize:9,color:t.color,fontWeight:700,marginTop:2}}>+{t.rate.toFixed(2)}/hr</div>}
                    </div>
                    <span style={{fontSize:22,fontWeight:900,color:t.color,fontFamily:'monospace'}}>{t.val.toFixed(1)}</span>
                  </div>
                ))}
                {waxActor&&bhoomRate===0&&<p style={{fontSize:10,color:'#334155',marginTop:8}}>Own land to start earning Bhoomi</p>}
                {bhoomRate>0&&<p style={{fontSize:9,color:'#475569',marginTop:8}}>Buy seeds with Bhoomi → Manage Your Plots</p>}
                {/* Log inventory */}
                {waxActor&&Object.values(myLogs).some(v=>v>0)&&(
                  <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(255,255,255,0.04)'}}>
                    <p style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:6}}>🪵 Logs Inventory</p>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                      {Object.entries(myLogs).filter(([,v])=>v>0).map(([si,qty])=>{
                        const cfg=TREE_HARVEST[parseInt(si)];
                        return cfg?(
                          <div key={si} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,background:'rgba(134,239,172,0.06)',border:'1px solid rgba(134,239,172,0.15)',borderRadius:6,padding:'3px 7px',color:'#86efac'}}>
                            <span style={{fontWeight:700}}>{qty}×</span><span style={{color:'#64748b'}}>{cfg.name}</span>
                          </div>
                        ):null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </StepCard>

            {/* Step 5 */}
            <StepCard number={5} title="Colony & Leaderboard">
              <div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,marginBottom:12}}>
                  {[{v:totalOwned,l:'Owned',c:'#f59e0b'},{v:planted,l:'Planted',c:'#4ade80'},{v:Object.keys(acctCount).length,l:'Owners',c:'#93c5fd'}].map(s=>(
                    <div key={s.l} style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'8px 6px',textAlign:'center'}}>
                      <div style={{fontWeight:900,fontSize:17,color:s.c,lineHeight:1}}>{s.v}</div>
                      <div style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.08em',marginTop:3}}>{s.l}</div>
                    </div>
                  ))}
                </div>
                {Object.keys(spCounts).length>0&&(
                  <div style={{marginBottom:12}}>
                    <p style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6,fontWeight:700}}>Species Growing</p>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                      {SPECIES.filter(s=>spCounts[s.id]).map(sp=>(
                        <div key={sp.id} style={{display:'flex',alignItems:'center',gap:4,fontSize:11,background:'rgba(255,255,255,0.04)',borderRadius:6,padding:'3px 8px'}}>
                          <span>{sp.emoji}</span><span style={{color:'#94a3b8'}}>{spCounts[sp.id]}×</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {Object.keys(acctCount).length>0&&(
                  <div style={{marginBottom:12}}>
                    <p style={{fontSize:9,color:'#334155',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6,fontWeight:700}}>Landowners · Bhoomi</p>
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      {Object.entries(acctCount).sort((a,b)=>b[1]-a[1]).map(([acct,cnt])=>{
                        const lb=lbRows.find((r:any)=>r.wax_account===acct);
                        const bh=lb?parseFloat(lb.bhoomi_score)||0:0;
                        return (
                          <div key={acct} style={{display:'flex',alignItems:'center',gap:8,fontSize:11}}>
                            <div style={{width:7,height:7,borderRadius:'50%',flexShrink:0,background:acctColor[acct]}}/>
                            <span style={{fontFamily:'monospace',color:'#cbd5e1',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{acct}</span>
                            <span style={{color:'#475569',flexShrink:0,fontSize:10}}>{cnt}p</span>
                            {bh>0&&<span style={{color:'#d97706',flexShrink:0,fontWeight:700,fontSize:10}}>🪙{bh.toFixed(1)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <button onClick={onLeaderboard} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 12px',borderRadius:10,border:'1px solid rgba(245,158,11,0.25)',background:'rgba(245,158,11,0.06)',color:'#fcd34d',fontSize:12,fontWeight:700,cursor:'pointer'}}
                  onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.14)';}}
                  onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='rgba(245,158,11,0.06)';}}
                ><Trophy size={13}/> View Full Leaderboard</button>
              </div>
            </StepCard>

            <div style={{marginTop:8,paddingTop:12,borderTop:'1px solid rgba(255,255,255,0.04)'}}>
              <button onClick={handleResetCache}
                style={{width:'100%',padding:'7px',borderRadius:9,border:'1px solid rgba(239,68,68,0.12)',background:'rgba(239,68,68,0.04)',color:'rgba(239,68,68,0.5)',fontSize:10,fontWeight:700,cursor:'pointer',letterSpacing:'0.05em'}}
                onMouseEnter={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.color='#f87171';b.style.borderColor='rgba(239,68,68,0.3)';b.style.background='rgba(239,68,68,0.08)';}}
                onMouseLeave={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.color='rgba(239,68,68,0.5)';b.style.borderColor='rgba(239,68,68,0.12)';b.style.background='rgba(239,68,68,0.04)';}}
              >↺ Reset Browser Cache</button>
              <p style={{fontSize:9,color:'#1e293b',marginTop:5,textAlign:'center'}}>Clears local storage · Database is safe</p>
            </div>
          </div>
        </div>
        )}

      </div>

      {/* Plot Manager Overlay */}
      {managePlotOpen&&myAllPlots.length>0&&(
        <PlotManagerOverlay
          plots={myAllPlots}
          cursor={managePlotCursor}
          onCursor={setManagePlotCursor}
          onClose={()=>setManagePlotOpen(false)}
          allCoords={allCoords}
          gs={gs}
          ownerByPlot={ownerByPlot}
          acctColor={acctColor}
          myAcct={myAcct}
          bhoomAccum={bhoomAccum}
          mySeedCount={mySeedCount}
          onPlant={(pidx,spp)=>{plantSpecies(pidx,spp);setManagePlotOpen(false);}}
          onRemove={(pidx)=>{setPlot(pidx,null,waxActor);}}
        />
      )}

      {/* ── Plant Seed Modal ── */}
      {plantSeedModal&&(
        <>
          <div style={{position:'fixed',inset:0,zIndex:200,background:'rgba(5,4,2,0.85)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center'}}
            onClick={()=>{if(plantSeedStatus!=='sending'){setPlantSeedModal(null);setPlantSeedSpecies(null);setPlantSeedStatus('idle');setPlantSeedMsg('');}}}>
            <div style={{background:'rgba(12,9,3,0.98)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:18,padding:'24px 28px',width:460,maxWidth:'92vw',maxHeight:'85vh',overflowY:'auto',boxShadow:'0 12px 80px rgba(0,0,0,0.8)'}}
              onClick={e=>e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:24}}>🌱</span>
                  <div>
                    <p style={{color:'#c4b5fd',fontSize:15,fontWeight:900,margin:0}}>Plant Seed</p>
                    <p style={{color:'#7c3aed',fontSize:9,margin:0,fontWeight:700,letterSpacing:2,textTransform:'uppercase'}}>Plot #{plantSeedModal.plotIdx+1} · {plantSeedModal.slots} slot{plantSeedModal.slots!==1?'s':''} free</p>
                  </div>
                </div>
                <button onClick={()=>{setPlantSeedModal(null);setPlantSeedSpecies(null);setPlantSeedStatus('idle');setPlantSeedMsg('');}}
                  style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#ffffff60',fontSize:18,width:32,height:32,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
              </div>

              {plantSeedModal.landowner!==myAcct&&(
                <div style={{background:'rgba(139,92,246,0.07)',border:'1px solid rgba(139,92,246,0.2)',borderRadius:10,padding:'8px 12px',marginBottom:14,fontSize:10,color:'#a78bfa'}}>
                  🏜️ Landowner: <span style={{fontWeight:700,fontFamily:'monospace'}}>{plantSeedModal.landowner}</span> · You earn <span style={{color:'#f472b6',fontWeight:700}}>0.8 Food/hr</span> per tree · They earn <span style={{color:'#34d399',fontWeight:700}}>0.2 Food/hr</span> passively
                </div>
              )}

              <p style={{fontSize:9,color:'#7c3aed',textTransform:'uppercase',letterSpacing:'0.1em',fontWeight:700,marginBottom:10}}>Choose Species</p>
              <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:16}}>
                {SPECIES.map(sp=>{
                  const si=SPECIES_ID_TO_INDEX[sp.id];
                  if(!si) return null;
                  const isSelected=plantSeedSpecies===si;
                  return (
                    <div key={sp.id} onClick={()=>setPlantSeedSpecies(si)}
                      style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',borderRadius:10,border:`1px solid ${isSelected?'rgba(139,92,246,0.5)':'rgba(255,255,255,0.07)'}`,background:isSelected?'rgba(139,92,246,0.12)':'rgba(255,255,255,0.03)',cursor:'pointer',transition:'all 0.15s'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:20}}>{sp.emoji}</span>
                        <div>
                          <div style={{color:'white',fontSize:12,fontWeight:700}}>{sp.name}</div>
                          <div style={{fontSize:9,color:'#475569',marginTop:1}}>
                            Matures: {TREE_HARVEST[si]?`${(TREE_HARVEST[si].maturity_sec/3600)<24?`${TREE_HARVEST[si].maturity_sec/3600}h`:`${TREE_HARVEST[si].maturity_sec/86400}d`}`:''} · Logs: {TREE_HARVEST[si]?.logs}
                          </div>
                        </div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{color:'#f472b6',fontSize:12,fontWeight:900}}>{plantSeedModal.landowner!==myAcct?FOOD_RATE[sp.id]!==undefined?`${(FOOD_RATE[sp.id]*0.8).toFixed(1)}`:'?':FOOD_RATE[sp.id]??'?'} <span style={{fontSize:9,color:'#475569'}}>food/hr</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,fontSize:10,color:'#475569'}}>
                <span>🌱 Seeds available: <span style={{color:'#a78bfa',fontWeight:700}}>{mySeedCount}</span></span>
                {mySeedCount===0&&<span style={{color:'#f87171'}}>Buy seeds for 1 WAX each (memo: season0:seed:1)</span>}
              </div>

              <button onClick={handlePlantSeed}
                disabled={plantSeedSpecies===null||mySeedCount<1||plantSeedStatus==='sending'}
                style={{width:'100%',padding:'10px',borderRadius:11,background:plantSeedSpecies&&mySeedCount>0?'linear-gradient(135deg,#7c3aed,#6d28d9)':'rgba(255,255,255,0.05)',border:'none',color:plantSeedSpecies&&mySeedCount>0?'white':'#334155',fontSize:13,fontWeight:900,cursor:plantSeedSpecies&&mySeedCount>0?'pointer':'not-allowed',transition:'all 0.2s'}}
              >{plantSeedStatus==='sending'?'Planting…':'🌱 Plant Seed'}</button>
              {plantSeedMsg&&<p style={{fontSize:11,marginTop:8,color:plantSeedStatus==='success'?'#86efac':'#f87171',textAlign:'center'}}>{plantSeedMsg}</p>}
            </div>
          </div>
        </>
      )}

      {/* Mobile bottom sheet — plot actions */}
      {isMobile&&mobilePlotSheet!==null&&selInfo&&(
        <>
          <div style={{position:'fixed',inset:0,zIndex:50,background:'rgba(0,0,0,0.5)'}} onClick={()=>{setMobilePlotSheet(null);setSel(null);}}/>
          <div style={{position:'fixed',bottom:56,left:0,right:0,zIndex:51,background:'rgba(8,14,28,0.98)',borderTop:'1px solid rgba(255,255,255,0.1)',borderRadius:'18px 18px 0 0',padding:'6px 0 0',maxHeight:'78vh',overflowY:'auto',boxShadow:'0 -8px 32px rgba(0,0,0,0.6)'}}>
            {/* Pull handle */}
            <div style={{width:36,height:4,borderRadius:2,background:'rgba(255,255,255,0.15)',margin:'0 auto 14px'}}/>
            <div style={{padding:'0 20px 24px'}}>
              {/* Plot header */}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div>
                  <div style={{fontWeight:800,fontSize:16,color:'white'}}>Plot #{mobilePlotSheet+1}</div>
                  <div style={{fontSize:11,color:'#475569',fontFamily:'monospace',marginTop:2}}>({selInfo.coord[0]}, {selInfo.coord[1]}) · {selInfo.owner||'Available'}</div>
                </div>
                <button onClick={()=>{setMobilePlotSheet(null);setSel(null);}} style={{color:'#475569',background:'none',border:'none',fontSize:20,cursor:'pointer',padding:'0 4px'}}>✕</button>
              </div>
              {/* Species / infra status */}
              {selInfo.ps&&selInfo.sp&&selInfo.stage!==null&&(
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',marginBottom:12}}>
                  <span style={{fontSize:28}}>{selInfo.sp.emoji}</span>
                  <div>
                    <div style={{fontWeight:700,color:'white',fontSize:13}}>{selInfo.sp.name}</div>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginTop:2}}>
                      <div style={{width:6,height:6,borderRadius:'50%',background:STAGE_COLORS[selInfo.stage]}}/>
                      <span style={{fontSize:11,color:STAGE_COLORS[selInfo.stage]}}>{STAGE_NAMES[selInfo.stage]}</span>
                      <span style={{fontSize:11,color:'#f472b6'}}>· 🍎+{FOOD_RATE[selInfo.sp.id]??0}/hr</span>
                    </div>
                  </div>
                </div>
              )}
              {selInfo.infraType&&(
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:'rgba(6,182,212,0.07)',border:'1px solid rgba(6,182,212,0.2)',marginBottom:12}}>
                  <span style={{fontSize:26}}>{INFRA_PAL[selInfo.infraType]?.emoji??'🏗️'}</span>
                  <div>
                    <div style={{fontWeight:700,color:'white',fontSize:13}}>{selInfo.infraType==='water_harvester'?'Water Harvester':'Hand Cart'}</div>
                    <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{selInfo.infraType==='water_harvester'?'+10% food rate · active':'Transport enabled · active'}</div>
                  </div>
                </div>
              )}
              {/* Actions for my plots */}
              {selInfo.isMine&&(
                <>
                  {/* Empty plot — species grid */}
                  {!selInfo.ps&&!selInfo.infraType&&(
                    <>
                      <div style={{padding:'10px',borderRadius:8,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',textAlign:'center',marginBottom:12}}>
                        <p style={{fontSize:11,color:'#475569',fontWeight:700}}>🚫 Planting closed — use Atavi Forest</p>
                      </div>
                      {/* Infra placement */}
                      {(myInfra.machine_owned&&(myInfra.placed['water_harvester']??0)===0)||(myInfra.handcart_owned&&(myInfra.placed['handcart']??0)===0)?(<>
                        <p style={{fontSize:11,color:'#475569',marginBottom:8,fontWeight:600}}>🏗️ Or place infrastructure:</p>
                        <div style={{display:'flex',flexDirection:'column',gap:6}}>
                          {myInfra.machine_owned&&(myInfra.placed['water_harvester']??0)===0&&(
                            <button onClick={()=>{handlePlaceInfra(mobilePlotSheet,'water_harvester');setMobilePlotSheet(null);setSel(null);}}
                              style={{display:'flex',alignItems:'center',gap:10,padding:'11px 14px',borderRadius:10,border:'1px solid rgba(6,182,212,0.3)',background:'rgba(6,182,212,0.08)',color:'#67e8f9',fontSize:13,fontWeight:700,cursor:'pointer'}}
                            >💧 Place Water Harvester</button>
                          )}
                          {myInfra.handcart_owned&&(myInfra.placed['handcart']??0)===0&&(
                            <button onClick={()=>{handlePlaceInfra(mobilePlotSheet,'handcart');setMobilePlotSheet(null);setSel(null);}}
                              style={{display:'flex',alignItems:'center',gap:10,padding:'11px 14px',borderRadius:10,border:'1px solid rgba(249,115,22,0.3)',background:'rgba(249,115,22,0.08)',color:'#fdba74',fontSize:13,fontWeight:700,cursor:'pointer'}}
                            >🛒 Place Hand Cart</button>
                          )}
                        </div>
                      </>):null}
                    </>
                  )}
                  {/* Has tree — harvest + remove */}
                  {selInfo.ps&&!selInfo.infraType&&(
                    <>
                      {/* Maturity countdown — always visible */}
                      {!selInfo.isMature&&(
                        <div style={{width:'100%',padding:'10px',borderRadius:10,border:'1px solid rgba(100,116,139,0.2)',background:'rgba(100,116,139,0.05)',color:'#94a3b8',fontSize:11,marginBottom:6,textAlign:'center'}}>
                          🕐 Mature in {fmtDuration(selInfo.matureInSec)}
                        </div>
                      )}
                      {selInfo.isMature&&(
                        <button onClick={()=>{handleHarvest(mobilePlotSheet);setMobilePlotSheet(null);setSel(null);}} disabled={harvesting===mobilePlotSheet}
                          style={{width:'100%',padding:'12px',borderRadius:10,border:'1px solid rgba(134,239,172,0.3)',background:'rgba(134,239,172,0.08)',color:'#86efac',fontSize:13,fontWeight:700,cursor:'pointer',marginBottom:6}}
                        >🪵 Harvest Tree</button>
                      )}
                      <button onClick={()=>{setPlot(mobilePlotSheet,null,waxActor);setMobilePlotSheet(null);setSel(null);}}
                        style={{width:'100%',padding:'12px',borderRadius:10,border:'1px solid rgba(239,68,68,0.2)',background:'rgba(239,68,68,0.06)',color:'#f87171',fontSize:13,fontWeight:700,cursor:'pointer',marginTop:4}}
                      >Remove Plant</button>
                    </>
                  )}
                  {/* Has infra — remove */}
                  {selInfo.infraType&&(
                    <button onClick={()=>{handleUnplaceInfra(mobilePlotSheet);setMobilePlotSheet(null);setSel(null);}}
                      style={{width:'100%',padding:'12px',borderRadius:10,border:'1px solid rgba(239,68,68,0.2)',background:'rgba(239,68,68,0.06)',color:'#f87171',fontSize:13,fontWeight:700,cursor:'pointer',marginTop:4}}
                    >Remove {selInfo.infraType==='water_harvester'?'Water Harvester':'Hand Cart'}</button>
                  )}
                </>
              )}
              {!selInfo.owner&&(
                <p style={{fontSize:12,color:'#475569',textAlign:'center',marginTop:4}}>This plot is available — buy land to claim it</p>
              )}
              {selInfo.owner&&!selInfo.isMine&&(
                <p style={{fontSize:12,color:'#475569',textAlign:'center',marginTop:4}}>Owned by <span style={{color:'white',fontFamily:'monospace'}}>{selInfo.owner}</span></p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Radial species picker — desktop only */}
      {!isMobile&&radialMenu&&(
        <>
          <div style={{position:'fixed',inset:0,zIndex:40}} onClick={()=>setRadialMenu(null)}/>
          <div style={{position:'fixed',left:radialMenu.x,top:radialMenu.y,zIndex:41,pointerEvents:'none'}}>
            <div style={{position:'absolute',left:-36,top:-36,width:72,height:72,borderRadius:'50%',background:'rgba(15,23,42,0.92)',border:`2px solid ${mySeedCount>0?'rgba(167,139,250,0.45)':'rgba(74,222,128,0.35)'}`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',fontSize:10,color:mySeedCount>0?'#a78bfa':'#4ade80',fontWeight:700,pointerEvents:'none',boxShadow:'0 0 20px rgba(0,0,0,0.6)',animation:'radialIn 0.2s ease'}}>
              <span style={{fontSize:14}}>{mySeedCount>0?'🌱':'🪙'}</span><span style={{marginTop:2}}>{mySeedCount>0?`${mySeedCount}s`:'Bhoomi'}</span>
            </div>
            {SPECIES.map((sp,i)=>{
              const angle=(i*(Math.PI*2/8))-(Math.PI/2);
              const r=90, bx=Math.round(Math.cos(angle)*r), by=Math.round(Math.sin(angle)*r);
              return (
                <button key={sp.id} disabled title="Planting closed"
                  style={{position:'absolute',left:bx-26,top:by-26,width:52,height:52,borderRadius:'50%',background:'rgba(15,23,42,0.95)',border:'2px solid rgba(255,255,255,0.08)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'not-allowed',pointerEvents:'all',boxShadow:'0 2px 12px rgba(0,0,0,0.5)',animation:`radialIn 0.2s ease ${i*0.02}s both`,gap:1,opacity:0.3}}
                >
                  <span style={{fontSize:18,lineHeight:1,pointerEvents:'none'}}>{sp.emoji}</span>
                  <span style={{fontSize:7,color:'#475569',fontWeight:700,pointerEvents:'none',textAlign:'center',lineHeight:1.1}}>closed</span>
                </button>
              );
            })}
            {/* Outer ring — infra items (only shown when player owns unplaced ones) */}
            {(()=>{
              const items:[string,typeof INFRA_PAL[string],string][] = [];
              if(myInfra.machine_owned&&(myInfra.placed['water_harvester']??0)===0) items.push(['water_harvester',INFRA_PAL.water_harvester,'Harvester']);
              if(myInfra.handcart_owned&&(myInfra.placed['handcart']??0)===0) items.push(['handcart',INFRA_PAL.handcart,'Hand Cart']);
              if(!items.length) return null;
              return items.map(([key,pal,label],i)=>{
                const angle=(i*(Math.PI*2/items.length))-(Math.PI/2);
                const r=155, bx=Math.round(Math.cos(angle)*r), by=Math.round(Math.sin(angle)*r);
                return (
                  <button key={key} onClick={e=>{e.stopPropagation();handlePlaceInfra(radialMenu.plotIdx,key);setRadialMenu(null);}}
                    title={label}
                    style={{position:'absolute',left:bx-29,top:by-29,width:58,height:58,borderRadius:'50%',background:'rgba(15,23,42,0.95)',border:`2px solid ${pal.glow}88`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'pointer',pointerEvents:'all',boxShadow:`0 2px 14px rgba(0,0,0,0.6),0 0 10px ${pal.glow}33`,animation:`radialIn 0.22s ease ${i*0.04+0.05}s both`,transition:'transform 0.1s,border-color 0.1s,box-shadow 0.1s',gap:2}}
                    onMouseEnter={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.transform='scale(1.15)';b.style.borderColor=pal.glow;b.style.boxShadow=`0 2px 18px rgba(0,0,0,0.6),0 0 16px ${pal.glow}66`;}}
                    onMouseLeave={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.transform='scale(1)';b.style.borderColor=`${pal.glow}88`;b.style.boxShadow=`0 2px 14px rgba(0,0,0,0.6),0 0 10px ${pal.glow}33`;}}
                  >
                    <span style={{fontSize:22,lineHeight:1,pointerEvents:'none'}}>{pal.emoji}</span>
                    <span style={{fontSize:7,color:pal.glow,fontWeight:700,pointerEvents:'none',textAlign:'center',lineHeight:1.1}}>{label}</span>
                  </button>
                );
              });
            })()}
          </div>
        </>
      )}
    </div>
  );
};

// ─── PlotManagerOverlay ───────────────────────────────────────────────────────
function PlotManagerOverlay({
  plots, cursor, onCursor, onClose,
  allCoords, gs, ownerByPlot, acctColor, myAcct, bhoomAccum, mySeedCount,
  onPlant, onRemove,
}: {
  plots: number[]; cursor: number; onCursor: (n: number) => void; onClose: () => void;
  allCoords: [number,number][]; gs: GameState; ownerByPlot: Record<number,string>;
  acctColor: Record<string,string>; myAcct: string; bhoomAccum: number; mySeedCount: number;
  onPlant: (pidx: number, sp: typeof SPECIES[0]) => void;
  onRemove: (pidx: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pidx = plots[cursor] ?? plots[0];
  const ps = gs[pidx] ?? null;
  const sp = ps ? SPECIES.find(s => s.id === ps.species) ?? null : null;
  const pal = sp ? (SPECIES_PAL[ps!.species] ?? SPECIES_PAL.mesquite) : null;
  const owner = ownerByPlot[pidx] ?? null;
  const ownerColor = owner ? acctColor[owner] ?? '#f59e0b' : '#f59e0b';
  const stage = ps ? getStage(ps.species, ps.plantedAt) : null;
  const coord = allCoords[pidx] ?? [0, 0];

  // Refs so RAF loop sees latest values without restart
  const stateRef = useRef({ pidx, ps, sp, pal, owner, ownerColor, stage });
  useEffect(() => { stateRef.current = { pidx, ps, sp, pal, owner, ownerColor, stage }; }, [pidx, ps, sp, pal, owner, ownerColor, stage]);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    let rafId: number;
    const frame = (now: number) => {
      const W = canvas.offsetWidth, H = canvas.offsetHeight;
      if (Math.round(canvas.width) !== Math.round(W * dpr) || Math.round(canvas.height) !== Math.round(H * dpr)) {
        canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
        canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      }
      const ctx = canvas.getContext('2d')!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const { pidx: pi, ps: pss, sp: spp, pal: palc, owner: own, ownerColor: oc } = stateRef.current;

      // Starfield background
      ctx.fillStyle = 'rgba(2,6,23,0.6)';
      ctx.fillRect(0, 0, W, H);

      const sc = 4;
      ctx.save();
      ctx.translate(W / 2, H / 2 + 20);
      ctx.scale(sc, sc);

      let hKey = own ? 'bare' : 'av';
      if (pss) { const st = getStage(pss.species, pss.plantedAt); hKey = `s${st}`; }
      const hPx = TILE_H[hKey] ?? TILE_H.av;

      let topC: string, southC: string, eastC: string, glowC: string | null;
      if (palc) { topC = palc.top; southC = palc.south; eastC = palc.east; glowC = palc.glow; }
      else if (own) { topC = oc + '88'; southC = oc + '44'; eastC = oc + '22'; glowC = oc; }
      else { topC = '#c49038'; southC = '#8a6420'; eastC = '#6b4c18'; glowC = '#f59e0b'; }

      const shimPhase = (((now * 0.4 + pi * 41) % 3000) / 3000);
      const glowAmt = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(now * 0.002 + pi * 0.27));
      const glowStr = glowAmt * (palc ? 18 : 12);
      const emoji = spp?.emoji ?? null;
      const stg = pss ? getStage(pss.species, pss.plantedAt) : null;
      const emojiSz = stg === 3 ? 15 : stg === 2 ? 13 : stg === 1 ? 11 : 9;

      drawIsoTile(ctx, 0, 0, hPx, topC, southC, eastC, glowC, glowStr, emoji, emojiSz, true, pi === 0, !!own, shimPhase);
      ctx.restore();

      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafId);
  }, []); // runs once; reads state from ref

  const prev = () => onCursor((cursor - 1 + plots.length) % plots.length);
  const next = () => onCursor((cursor + 1) % plots.length);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(2,6,23,0.93)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Close */}
      <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#94a3b8', fontSize: 16, width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>

      {/* Header */}
      <p style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 4 }}>Manage Your Plots</p>
      <p style={{ fontSize: 18, fontWeight: 900, color: 'white', marginBottom: 2 }}>Plot #{pidx + 1}</p>
      <p style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', marginBottom: 4 }}>({coord[0]}, {coord[1]})</p>
      {plots.length > 1 && <p style={{ fontSize: 10, color: '#334155', marginBottom: 14 }}>{cursor + 1} of {plots.length}</p>}

      {/* Canvas + arrows */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
        <button onClick={prev} disabled={plots.length <= 1} style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: plots.length <= 1 ? '#1e293b' : '#94a3b8', fontSize: 22, cursor: plots.length <= 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>‹</button>
        <div style={{ position: 'relative', width: 320, height: 300 }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block', borderRadius: 16 }} />
          {sp && stage !== null && (
            <div style={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(15,23,42,0.88)', border: `1px solid ${sp.color}55`, borderRadius: 20, padding: '4px 12px' }}>
                <span style={{ fontSize: 14 }}>{sp.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'white' }}>{sp.name}</span>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: STAGE_COLORS[stage] }} />
                <span style={{ fontSize: 10, color: STAGE_COLORS[stage] }}>{STAGE_NAMES[stage]}</span>
                <span style={{ fontSize: 10, color: '#475569' }}>+{sp.mic[stage]}/hr</span>
              </div>
            </div>
          )}
          {!ps && owner && (
            <div style={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(15,23,42,0.88)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 20, padding: '4px 14px', fontWeight: 700 }}>Vacant · +0.1 pts/hr</div>
            </div>
          )}
        </div>
        <button onClick={next} disabled={plots.length <= 1} style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: plots.length <= 1 ? '#1e293b' : '#94a3b8', fontSize: 22, cursor: plots.length <= 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>›</button>
      </div>

      {/* Dot nav — capped at 12 */}
      {plots.length > 1 && plots.length <= 12 && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 18 }}>
          {plots.map((_, i) => (
            <button key={i} onClick={() => onCursor(i)} style={{ width: i === cursor ? 18 : 7, height: 7, borderRadius: 999, background: i === cursor ? '#f59e0b' : 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', padding: 0, transition: 'width 0.18s, background 0.18s' }} />
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ width: 300, maxWidth: '90vw' }}>
        {myAcct === owner && !ps && (
          <div style={{ padding: '10px', borderRadius: 8, border: '1px solid rgba(100,116,139,0.2)', background: 'rgba(100,116,139,0.05)', textAlign: 'center' }}>
            <p style={{ fontSize: 11, color: '#475569', fontWeight: 700 }}>🚫 Planting closed — use Atavi Forest</p>
          </div>
        )}
        {myAcct === owner && ps && (
          <div style={{ textAlign: 'center', paddingTop: 4 }}>
            <p style={{ fontSize: 10, color: '#475569', marginBottom: 12 }}>Plant is growing. Remove to replant.</p>
            <button onClick={() => onRemove(pidx)}
              style={{ padding: '9px 28px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.07)', color: 'rgba(239,68,68,0.7)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#f87171'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239,68,68,0.4)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(239,68,68,0.7)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239,68,68,0.2)'; }}
            >🪓 Remove Plant</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── StepCard ─────────────────────────────────────────────────────────────────
function StepCard({number,title,done,locked,children}:{number:number;title:string;done?:boolean;locked?:boolean;children:React.ReactNode}) {
  return (
    <div style={{marginBottom:14,borderRadius:12,border:`1px solid ${done?'rgba(74,222,128,0.2)':locked?'rgba(255,255,255,0.04)':'rgba(255,255,255,0.09)'}`,background:locked?'rgba(255,255,255,0.01)':'rgba(15,23,42,0.5)',overflow:'hidden',opacity:locked?0.45:1}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',borderBottom:locked?'none':'1px solid rgba(255,255,255,0.05)',background:done?'rgba(74,222,128,0.04)':'transparent'}}>
        <div style={{width:22,height:22,borderRadius:'50%',flexShrink:0,background:done?'#4ade80':locked?'rgba(255,255,255,0.06)':'rgba(245,158,11,0.12)',border:done?'none':`1px solid ${locked?'rgba(255,255,255,0.08)':'rgba(245,158,11,0.25)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:done?'#020617':locked?'#1e293b':'#fcd34d'}}>
          {done?'✓':number}
        </div>
        <span style={{fontSize:12,fontWeight:700,color:done?'#4ade80':locked?'#1e293b':'white'}}>{title}</span>
      </div>
      {!locked&&<div style={{padding:'12px 14px'}}>{children}</div>}
    </div>
  );
}

export default GamePage;
